import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { WO_TYPE_COLORS, WO_TYPE_LABELS, statusColors, priorityColors, PRIORITIES } from '../utils/constants';
import { FiArrowLeft, FiArrowRight, FiEdit2, FiSave, FiX, FiUpload, FiClock, FiUser, FiCalendar, FiPaperclip, FiMessageSquare, FiTrash2, FiCheckSquare, FiCheck, FiPlus, FiChevronUp, FiChevronDown, FiChevronRight, FiRefreshCw, FiLayers, FiEye, FiGitBranch, FiFileText, FiBox, FiCornerDownLeft, FiCamera, FiDownload, FiPrinter, FiZoomIn, FiZoomOut, FiSearch, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import MediaViewer from '../components/MediaViewer';
import WorkOrderCreateModal from '../components/WorkOrderCreateModal';
import TimeConfirmModal from '../components/TimeConfirmModal';
import UploadProgress from '../components/UploadProgress';
import MaterialReimbursements from '../components/MaterialReimbursements';
import titleCase from '../utils/titleCase';
import { playSuccess, playClick, playDelete } from '../utils/sounds';
import { useToast } from '../context/ToastContext';

const playCheckSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1174, ctx.currentTime + 0.06);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  } catch {}
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function fmtMins(totalMins) {
  const abs = Math.abs(totalMins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function getTrackingUrl(supplier, number) {
  if (!supplier || !number) return null;
  const s = supplier.toLowerCase();
  if (s.includes('auspost') || s.includes('australia post')) return `https://auspost.com.au/mypost/track/#/details/${number}`;
  if (s.includes('dhl')) return `https://www.dhl.com/au-en/home/tracking.html?tracking-id=${number}`;
  if (s.includes('startrack') || s.includes('star track')) return `https://startrack.com.au/track/${number}`;
  if (s.includes('toll')) return `https://www.mytoll.com/track/#${number}`;
  if (s.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${number}`;
  if (s.includes('ups')) return `https://www.ups.com/track?tracknum=${number}`;
  if (s.includes('tnt')) return `https://www.tnt.com/express/en_au/site/tracking.html?searchType=con&cons=${number}`;
  return null;
}

function linkifyText(text) {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? React.createElement('a', { key: i, href: part, target: '_blank', rel: 'noopener noreferrer', style: { color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' } }, part)
      : part
  );
}

function stripHtml(str) {
  if (!str) return '';
  return str.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<\/div>/gi, '\n').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').trim();
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch, token, user, companyCode } = useAuth();
  const { showToast } = useToast();
  const fromCapacity = searchParams.get('from') === 'capacity';
  const capacityUserId = searchParams.get('user');
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [timeConfirmations, setTimeConfirmations] = useState([]);
  const [myTeam, setMyTeam] = useState([]);
  // All sections shown expanded (no tabs)
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showTimeConfirm, setShowTimeConfirm] = useState(false);
  // Load the supervisor's team so they can log time on behalf of a member.
  // Plain users get a 403 on /my-team — swallow silently.
  useEffect(() => {
    if (!showTimeConfirm) return;
    const canSupervise = user?.permissions?.includes('supervisor_panel')
      || user?.permissions?.includes('admin_panel');
    if (!canSupervise) { setMyTeam([]); return; }
    authFetch('/api/users/my-team')
      .then(r => r.ok ? r.json() : [])
      .then(rows => setMyTeam(Array.isArray(rows) ? rows.filter(u => !u.disabled && u.id !== user?.id) : []))
      .catch(() => setMyTeam([]));
  }, [showTimeConfirm, user?.id, user?.permissions, authFetch]);
  const [newComment, setNewComment] = useState('');
  const [commentFocused, setCommentFocused] = useState(false);
  const [commentFiles, setCommentFiles] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingTc, setEditingTc] = useState(null);
  const [editTcHours, setEditTcHours] = useState(0);
  const [editTcMinutes, setEditTcMinutes] = useState(0);
  const [editTcFinal, setEditTcFinal] = useState(false);
  const [cancelTcTarget, setCancelTcTarget] = useState(null);
  const [cancelTcReason, setCancelTcReason] = useState('');
  const [taskListItems, setTaskListItems] = useState([]);
  // History Compare panel (task-history endpoint, PM02 only) — lazy-loaded
  // the first time the user expands it.
  const [taskHistory, setTaskHistory] = useState(null);
  const [taskHistoryLoading, setTaskHistoryLoading] = useState(false);
  const [taskHistoryExpanded, setTaskHistoryExpanded] = useState(false);
  const [taskHistoryShowAll, setTaskHistoryShowAll] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [newTaskType, setNewTaskType] = useState('checkbox');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskLabel, setEditingTaskLabel] = useState('');
  const editingFieldRef = useRef(false); // prevents polling from overwriting inputs while typing
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [operations, setOperations] = useState([]);
  const [pendingOps, setPendingOps] = useState([{ _id: 0, title: '', date: '', hours: 0, minutes: 0, description: '' }]);
  const pendingOpIdRef = useRef(1);
  const [showAddOpModal, setShowAddOpModal] = useState(false);
  const [addOpSuccess, setAddOpSuccess] = useState(null); // { count: N } after successful submit
  const [editingOpId, setEditingOpId] = useState(null);
  const [editingOpTitle, setEditingOpTitle] = useState('');
  const [showEditOpModal, setShowEditOpModal] = useState(null);
  const [editOpForm, setEditOpForm] = useState({ title: '', planned_date: '', est_hours: 0, est_minutes: 0, assigned_to: '' });
  const [editOpCapacity, setEditOpCapacity] = useState([]);
  const [editOpCapRange, setEditOpCapRange] = useState(4);
  // Auto-clear planned date if capacity data shows it would exceed
  useEffect(() => {
    if (editOpCapacity.length === 0 || !editOpForm.planned_date) return;
    const jobH = (editOpForm.est_hours || 0) + (editOpForm.est_minutes || 0) / 60;
    const d = editOpCapacity.find(d => d.date === editOpForm.planned_date);
    if (d && d.available > 0 && (d.allocated >= d.available || (d.allocated + jobH) > d.available))
      setEditOpForm(f => ({ ...f, planned_date: '' }));
  }, [editOpCapacity, editOpForm.est_hours, editOpForm.est_minutes]);
  const [editOpUserSearch, setEditOpUserSearch] = useState('');
  const [editOpUserDropdownOpen, setEditOpUserDropdownOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [woTab, setWoTab] = useState('comments');
  const [editMode, setEditMode] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetResults, setAssetResults] = useState([]);
  const [assetSearching, setAssetSearching] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [inspectSnapshot, setInspectSnapshot] = useState(null);
  const [subWorkOrders, setSubWorkOrders] = useState([]);
  const [relatedWorkOrders, setRelatedWorkOrders] = useState([]);
  const [legacyComments, setLegacyComments] = useState([]);
  const [legacyWoNumber, setLegacyWoNumber] = useState(null);
  const [legacyWoDescription, setLegacyWoDescription] = useState('');
  const [showSubWoModal, setShowSubWoModal] = useState(false);
  const [showActionPicker, setShowActionPicker] = useState(false);
  const [actionPickerPos, setActionPickerPos] = useState({ top: 0, right: 0 });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [photoModal, setPhotoModal] = useState(null); // { url, zoom }
  const [photoZoom, setPhotoZoom] = useState(1);
  const [materials, setMaterials] = useState([]);
  const [datePickerPending, setDatePickerPending] = useState(null); // { matId, field, date, po_number }
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialForm, setMaterialForm] = useState({ material_type: 'part', part_name: '', part_number: '', quantity: 1, supplier_name: '', description: '', warranty_value: '', warranty_unit: 'months', cost: '', storage_location: '', company_name: '', price_type: 'total', price_per_hour: '', total_price: '', tracking_supplier: '', tracking_number: '' });

  // File upload
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileDragging, setFileDragging] = useState(false);
  const [uploadDescriptions, setUploadDescriptions] = useState([]);
  const [taskPhotoUploading, setTaskPhotoUploading] = useState(null); // itemId currently uploading
  const [taskPhotoProgress, setTaskPhotoProgress] = useState(0);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFileDesc, setEditingFileDesc] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [showOriginalComment, setShowOriginalComment] = useState(null);
  const [checklistOpIdx, setChecklistOpIdx] = useState(0);
  const [editLockError, setEditLockError] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const commentFileRef = useRef(null);
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  const [resolvedId, setResolvedId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isPhoneOrTablet = /iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTouch = (navigator.maxTouchPoints || 0) > 1;
    return window.innerWidth < 768 || isPhoneOrTablet || isTouch;
  });
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isPhoneOrTablet = /iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTouch = (navigator.maxTouchPoints || 0) > 1;
    const onResize = () => setIsMobile(window.innerWidth < 768 || isPhoneOrTablet || isTouch);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    const woId = decodeURIComponent(id).trim();
    // First resolve the param (could be wo_number or id) to get the actual issue
    setLoadError(null);
    authFetch(`/api/issues/${woId}`)
      .then(r => {
        if (!r.ok) return r.text().then(t => { throw new Error(`API ${r.status}: ${t.substring(0, 100)}`); });
        return r.json();
      })
      .then(issueData => {
        const realId = issueData.id;
        setResolvedId(realId);
        setIssue(issueData);
        if (issueData.type === 'PM02') setWoTab('tasklist');
        // Now load all related data using the resolved numeric id
        return Promise.all([
          authFetch(`/api/issues/${realId}/files`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/comments`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/history`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/time-confirmations`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch('/api/users').then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/task-list`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/operations`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/sub-work-orders`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/assignees`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/materials`).then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch(`/api/issues/${realId}/related-work-orders`).then(r => r.ok ? r.json() : []).catch(() => []),
        ]);
      })
      .then(([filesData, commentsData, historyData, tcData, usersData, taskListData, opsData, subWos, assigneesData, materialsData, relatedWos]) => {
        setFiles(filesData);
        setComments(commentsData);
        setHistory(historyData);
        setTimeConfirmations(tcData);
        setUsers(usersData);
        setTaskListItems(taskListData);
        setOperations(opsData);
        setSubWorkOrders(subWos);
        setAssignees(assigneesData);
        setMaterials(materialsData);
        setRelatedWorkOrders(relatedWos);
        // Fetch legacy WO comments if a legacy-linked WO exists in the chain
        const legacyWo = relatedWos.find(rwo => rwo.is_legacy_linked === 1);
        if (legacyWo) {
          setLegacyWoNumber(legacyWo.wo_number);
          authFetch(`/api/issues/${legacyWo.id}/comments`).then(r => r.ok ? r.json() : []).then(data => setLegacyComments(data)).catch(() => setLegacyComments([]));
          authFetch(`/api/issues/${legacyWo.id}`).then(r => r.ok ? r.json() : null).then(data => { if (data) setLegacyWoDescription(data.description || ''); }).catch(() => setLegacyWoDescription(''));
        } else {
          setLegacyComments([]);
          setLegacyWoNumber(null);
          setLegacyWoDescription('');
        }
        setLoading(false);
      }).catch(err => { console.error('WO load error:', err); setLoadError(err.message); setLoading(false); });
  }, [id]);

  // Poll for live updates every 15s
  useEffect(() => {
    if (!resolvedId) return;
    const poll = () => {
      if (!editingFieldRef.current) {
        authFetch(`/api/issues/${resolvedId}/operations`).then(r => r.ok ? r.json() : null).then(data => {
          if (data && !editingFieldRef.current) setOperations(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
        }).catch(() => {});
      }
      authFetch(`/api/issues/${resolvedId}/comments`).then(r => r.ok ? r.json() : null).then(data => {
        if (data) setComments(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
      }).catch(() => {});
      if (!editingFieldRef.current) {
        authFetch(`/api/issues/${resolvedId}/task-list`).then(r => r.ok ? r.json() : null).then(data => {
          if (data && !editingFieldRef.current) setTaskListItems(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
        }).catch(() => {});
      }
      authFetch(`/api/issues/${resolvedId}/time-confirmations`).then(r => r.ok ? r.json() : null).then(data => {
        if (data) setTimeConfirmations(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
      }).catch(() => {});
      authFetch(`/api/issues/${resolvedId}`).then(r => r.ok ? r.json() : null).then(data => {
        if (data) setIssue(prev => {
          if (prev && prev.updated_at !== data.updated_at) return data;
          return prev;
        });
      }).catch(() => {});
    };
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [resolvedId]);


  // Edit lock heartbeat + cleanup on unmount
  useEffect(() => {
    if (!editMode || !resolvedId) return;
    const hb = setInterval(() => {
      authFetch(`/api/issues/${resolvedId}/edit-lock/heartbeat`, { method: 'POST' }).catch(() => {});
    }, 60000);
    return () => {
      clearInterval(hb);
      authFetch(`/api/issues/${resolvedId}/edit-lock`, { method: 'DELETE' }).catch(() => {});
    };
  }, [editMode, resolvedId]);

  const handleTimeConfirmSubmit = async ({ hours, minutes, is_final, operation_id, worker_signature, work_date, on_behalf_of_user_id }) => {
    // Block final confirmation if task list items for this operation are not completely filled out
    if (is_final && taskListItems.length > 0) {
      // Find operation_number for this operation_id
      const op = operations.find(o => o.id === operation_id);
      const opNum = op?.operation_number;
      const opItems = opNum ? taskListItems.filter(i => i.operation_number === opNum) : taskListItems;
      const needsPhoto = opItems.filter(i => i.require_photo && getPhotos(i).length === 0);
      const noResult = opItems.filter(i => {
        const answered = i.item_type === 'checkbox' ? (i.value || i.checked) : (i.pfna || '');
        return !answered;
      });
      if (needsPhoto.length > 0 || noResult.length > 0) {
        const msgs = [];
        if (noResult.length > 0) msgs.push(`${noResult.length} task item(s) have no result`);
        if (needsPhoto.length > 0) msgs.push(`${needsPhoto.length} task item(s) missing required photo`);
        throw new Error(`Cannot submit final confirmation: ${msgs.join(', ')}`);
      }
    }
    const res = await authFetch(`/api/issues/${resolvedId}/time-confirmations`, {
      method: 'POST',
      body: JSON.stringify({ hours, minutes, is_final, operation_id, worker_signature: worker_signature || null, work_date, on_behalf_of_user_id: on_behalf_of_user_id || null })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to submit time confirmation');
    }
    const tc = await res.json();
    playSuccess();
    setTimeConfirmations(prev => [...prev, tc]);
    // Refresh issue and operations to get updated status
    const issueRes = await authFetch(`/api/issues/${resolvedId}`);
    if (issueRes.ok) setIssue(await issueRes.json());
    authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
    authFetch(`/api/issues/${resolvedId}/operations`).then(r => r.ok ? r.json() : []).then(setOperations);
    // After final confirmation, redirect back to My Work
    if (is_final) {
      setTimeout(() => navigate('/my-work'), 800);
    }
  };

  const handleDeleteTc = async (tcId) => {
    const res = await authFetch(`/api/issues/time-confirmations/${tcId}`, { method: 'DELETE' });
    if (res.ok) {
      setTimeConfirmations(prev => prev.filter(tc => tc.id !== tcId));
      const issueRes = await authFetch(`/api/issues/${resolvedId}`);
      if (issueRes.ok) setIssue(await issueRes.json());
    }
  };

  const handleCancelTc = async (tcId, reason) => {
    const res = await authFetch(`/api/issues/${resolvedId}/time-confirmations/cancel`, {
      method: 'POST', body: JSON.stringify({ tc_id: tcId, reason })
    });
    if (res.ok) {
      const reversal = await res.json();
      playSuccess();
      setTimeConfirmations(prev => [...prev, reversal]);
      setCancelTcTarget(null);
      setCancelTcReason('');
      const issueRes = await authFetch(`/api/issues/${resolvedId}`);
      if (issueRes.ok) setIssue(await issueRes.json());
      authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
      authFetch(`/api/issues/${resolvedId}/operations`).then(r => r.ok ? r.json() : []).then(setOperations);
    }
  };

  const handleEditTc = async (tcId) => {
    const res = await authFetch(`/api/issues/time-confirmations/${tcId}`, {
      method: 'PUT',
      body: JSON.stringify({ hours: editTcHours, minutes: editTcMinutes, is_final: editTcFinal })
    });
    if (res.ok) {
      const updated = await res.json();
      playSuccess();
      setTimeConfirmations(prev => prev.map(tc => tc.id === tcId ? { ...tc, ...updated } : tc));
      setEditingTc(null);
      const issueRes = await authFetch(`/api/issues/${resolvedId}`);
      if (issueRes.ok) setIssue(await issueRes.json());
    }
  };

  const handleSaveField = async (field, value) => {
    const res = await authFetch(`/api/issues/${resolvedId}`, {
      method: 'PUT',
      body: JSON.stringify({ [field]: value })
    });
    if (res.ok) {
      const updated = await res.json();
      setIssue(updated);
      authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
    }
    setEditing(null);
  };

  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    uploadFiles.forEach(f => formData.append('files', f));
    formData.append('descriptions', JSON.stringify(uploadDescriptions.map(d => d.trim())));

    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/issues/${resolvedId}/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        if (companyCode) xhr.setRequestHeader('X-Tenant-Code', companyCode);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const newFiles = JSON.parse(xhr.responseText);
            setFiles(prev => [...prev, ...newFiles]);
            resolve();
          } else {
            try { const d = JSON.parse(xhr.responseText); reject(new Error(d.error || 'Upload failed')); }
            catch { reject(new Error('Upload failed')); }
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });
    } catch (e) { alert(e.message || 'Upload failed. Please try again.'); }
    setUploading(false);
    setUploadFiles([]);
    setUploadDescriptions([]);
  };

  const handleEditFileDesc = async (fileId) => {
    const next = (editingFileDesc || '').trim();
    const res = await authFetch(`/api/issues/${resolvedId}/files/${fileId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: next })
    });
    if (res.ok) {
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, description: next || null } : f));
      setEditingFileId(null);
    }
  };

  const handleDeleteFile = (fileId) => {
    const file = files.find(f => f.id === fileId);
    setConfirmDialog({
      title: 'Delete attachment?',
      rows: [
        { label: 'File', value: file?.original_name || file?.file || `#${fileId}` },
        ...(file?.description ? [{ label: 'Description', value: file.description }] : []),
        { label: '', value: 'This cannot be undone.' },
      ],
      confirmLabel: 'Delete',
      confirmColor: '#dc2626',
      onConfirm: async () => {
        setConfirmDialog(null);
        const res = await authFetch(`/api/issues/${resolvedId}/files/${fileId}`, { method: 'DELETE' });
        if (res.ok) setFiles(prev => prev.filter(f => f.id !== fileId));
      },
    });
  };

  const handleEditComment = async (commentId) => {
    if (!editingCommentText.trim()) return;
    const res = await authFetch(`/api/issues/${resolvedId}/comments/${commentId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment: editingCommentText.trim() })
    });
    if (res.ok) {
      const updated = await res.json();
      playSuccess();
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
      setEditingCommentId(null);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    const trimmed = (newComment || '').trim();
    if (!trimmed && commentFiles.length === 0) return;
    if (trimmed.length > 0 && trimmed.length < 2) return;
    if (trimmed.length > 2000) return;
    setSubmittingComment(true);

    const formData = new FormData();
    formData.append('comment', newComment);
    commentFiles.forEach(f => formData.append('files', f));

    const res = await authFetch(`/api/issues/${resolvedId}/comments`, { method: 'POST', body: formData });
    if (res.ok) {
      const comment = await res.json();
      playSuccess();
      setComments(prev => [...prev, comment]);
      setNewComment('');
      setCommentFiles([]);
    }
    setSubmittingComment(false);
  };

  // Task list handlers
  const handleAddTaskItem = async () => {
    if (!newTaskLabel.trim()) return;
    const res = await authFetch(`/api/issues/${resolvedId}/task-list`, {
      method: 'POST', body: JSON.stringify({ label: newTaskLabel.trim(), item_type: newTaskType })
    });
    if (res.ok) {
      const item = await res.json();
      setTaskListItems(prev => [...prev, item]);
      setNewTaskLabel('');
    }
  };

  const handleCancelInspection = async () => {
    if (inspectSnapshot) {
      setTaskListItems(inspectSnapshot);
      // Restore server state
      authFetch('/api/issues/task-list/restore', {
        method: 'POST', body: JSON.stringify({ items: inspectSnapshot })
      }).catch(() => {});
    }
    setInspecting(false);
    setInspectSnapshot(null);
  };

  const handleToggleTaskItem = async (itemId) => {
    const res = await authFetch(`/api/issues/task-list/${itemId}/toggle`, { method: 'PATCH' });
    if (res.ok) {
      const updated = await res.json();
      setTaskListItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
    }
  };

  const handleUpdateTaskValue = async (itemId, value) => {
    const res = await authFetch(`/api/issues/task-list/${itemId}/value`, {
      method: 'PATCH', body: JSON.stringify({ value })
    });
    if (res.ok) {
      const updated = await res.json();
      setTaskListItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
    }
  };

  const handleUpdateTaskPfna = async (itemId, pfna) => {
    const res = await authFetch(`/api/issues/task-list/${itemId}/pfna`, {
      method: 'PATCH', body: JSON.stringify({ pfna })
    });
    if (res.ok) {
      const updated = await res.json();
      setTaskListItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
    }
  };

  const getPhotos = (item) => {
    if (!item.photo) return [];
    if (Array.isArray(item.photo)) return item.photo;
    try { const p = JSON.parse(item.photo); return Array.isArray(p) ? p : [item.photo]; } catch { return [item.photo]; }
  };

  const handleTaskItemPhoto = async (itemId, file) => {
    const allowedExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!file.type.startsWith('image/') && !allowedExts.includes(ext)) {
      alert('Only image files are allowed (JPG, PNG, GIF, WebP, BMP, HEIC)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo must be under 5MB');
      return;
    }
    const item = taskListItems.find(i => i.id === itemId);
    if (item && getPhotos(item).length >= 4) {
      alert('Maximum of 4 photos per task item');
      return;
    }
    setTaskPhotoUploading(itemId);
    setTaskPhotoProgress(0);
    const formData = new FormData();
    formData.append('photo', file);
    try {
      const updated = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/issues/task-list/${itemId}/photo`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        if (companyCode) xhr.setRequestHeader('X-Tenant-Code', companyCode);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setTaskPhotoProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try { const d = JSON.parse(xhr.responseText); reject(new Error(d.error || 'Upload failed')); }
            catch { reject(new Error('Upload failed')); }
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });
      setTaskListItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
    } catch (e) { alert(e.message || 'Upload failed'); }
    setTaskPhotoUploading(null);
    setTaskPhotoProgress(0);
  };

  const handleDeleteTaskPhoto = async (itemId, filename) => {
    const res = await authFetch(`/api/issues/task-list/${itemId}/photo/${encodeURIComponent(filename)}`, { method: 'DELETE' });
    if (res.ok) {
      const updated = await res.json();
      setTaskListItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
    }
  };

  const handleEditTaskItem = async (itemId) => {
    if (!editingTaskLabel.trim()) return;
    const res = await authFetch(`/api/issues/task-list/${itemId}`, {
      method: 'PUT', body: JSON.stringify({ label: editingTaskLabel.trim() })
    });
    if (res.ok) {
      setTaskListItems(prev => prev.map(i => i.id === itemId ? { ...i, label: editingTaskLabel.trim() } : i));
      setEditingTaskId(null);
    }
  };

  const handleDeleteTaskItem = async (itemId) => {
    const res = await authFetch(`/api/issues/task-list/${itemId}`, { method: 'DELETE' });
    if (res.ok) {
      setTaskListItems(prev => prev.filter(i => i.id !== itemId));
    }
  };

  const handleReorderTaskList = async (itemId, direction) => {
    const idx = taskListItems.findIndex(i => i.id === itemId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= taskListItems.length) return;
    const reordered = [...taskListItems];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const updates = reordered.map((item, i) => ({ id: item.id, sort_order: i }));
    setTaskListItems(reordered);
    await authFetch(`/api/issues/${resolvedId}/task-list/reorder`, {
      method: 'PUT', body: JSON.stringify(updates)
    });
  };


  const handleApplyTemplate = async (templateId) => {
    const res = await authFetch(`/api/issues/${resolvedId}/task-list/from-template`, {
      method: 'POST', body: JSON.stringify({ template_id: templateId })
    });
    if (res.ok) {
      const items = await res.json();
      setTaskListItems(items);
      const issueRes = await authFetch(`/api/issues/${resolvedId}`);
      if (issueRes.ok) setIssue(await issueRes.json());
    }
  };

  const loadTaskTemplates = async () => {
    const res = await authFetch('/api/issues/task-list-templates/all');
    if (res.ok) setTaskTemplates(await res.json());
  };

  const handleAddAssignee = async (userId, operationId, plannedHours) => {
    const res = await authFetch(`/api/issues/${resolvedId}/assignees`, {
      method: 'POST', body: JSON.stringify({ user_id: userId, operation_id: operationId || null, planned_hours: plannedHours || null })
    });
    if (res.ok) {
      const assignee = await res.json();
      setAssignees(prev => [...prev, assignee]);
      setShowAddAssignee(false);
    }
  };

  const handleRemoveAssignee = async (assigneeId) => {
    const res = await authFetch(`/api/issues/${resolvedId}/assignees/${assigneeId}`, { method: 'DELETE' });
    if (res.ok) setAssignees(prev => prev.filter(a => a.id !== assigneeId));
  };

  // For multi-op WOs, check if the current (last visible non-complete) operation has final confirmation
  const hasFinalConfirmation = (() => {
    if (operations.length <= 1) return timeConfirmations.some(tc => tc.is_final);
    // Find the current operation (first non-complete one)
    const currentOp = operations.find(op => {
      const hasFinal = timeConfirmations.filter(tc => tc.operation_id === op.id).some(tc => tc.is_final);
      return op.status !== 'Complete' && !hasFinal;
    });
    if (!currentOp) return timeConfirmations.some(tc => tc.is_final); // all ops complete
    return timeConfirmations.filter(tc => tc.operation_id === currentOp.id).some(tc => tc.is_final);
  })();
  const inspectActive = inspecting && !hasFinalConfirmation;

  // Per-operation access control for PM02 task list
  // Determine which operations the current user can edit (assigned + due within this week)
  // Check if a date falls within the current week or earlier
  const isInWeekOrPast = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const dow = now.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(now); mon.setDate(now.getDate() + monOff); mon.setHours(0,0,0,0);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6); sun.setHours(23,59,59,999);
    const planned = new Date(dateStr + 'T00:00:00');
    return planned <= sun;
  };
  const canEditOp = (opNumber) => {
    if (user?.permissions?.includes('admin_panel')) return true;
    if (user?.permissions?.includes('supervisor_panel')) return true;
    // For items without an operation, check WO-level assignment and due date
    if (opNumber === '_none') {
      const isWoAssigned = issue?.assigned_to === user?.id || assignees.some(a => a.user_id === user?.id);
      if (!isWoAssigned) return false;
      return isInWeekOrPast(issue?.due_date);
    }
    const op = operations.find(o => o.operation_number === opNumber);
    if (!op) return false;
    const isAssigned = op.assigned_to === user?.id || assignees.some(a => a.operation_id === op.id && a.user_id === user?.id);
    if (!isAssigned) return false;
    return isInWeekOrPast(op.planned_date);
  };

  // Can user confirm time on this WO? Must be assigned and WO/op planned within current working week
  const canConfirmTime = (() => {
    if (user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) return { allowed: true };
    const isWoAssigned = issue?.assigned_to === user?.id || assignees.some(a => a.user_id === user?.id);
    if (!isWoAssigned) return { allowed: false, reason: 'This work order is not assigned to you' };
    // Check at least one non-complete op is in-week
    const inWeekOp = operations.find(op => {
      if (op.status === 'Complete') return false;
      const isOpAssigned = !op.assigned_to || op.assigned_to === user?.id || assignees.some(a => a.operation_id === op.id && a.user_id === user?.id);
      return isOpAssigned && isInWeekOrPast(op.planned_date || issue?.due_date);
    });
    if (!inWeekOp) return { allowed: false, reason: 'No operations are scheduled within your current working week' };
    return { allowed: true };
  })();
  // Group task list items by operation_number
  const taskListByOp = (() => {
    const groups = {};
    taskListItems.forEach(item => {
      const opNum = item.operation_number || '_none';
      if (!groups[opNum]) groups[opNum] = [];
      groups[opNum].push(item);
    });
    // Sort by operation number
    return Object.keys(groups).sort().map(opNum => ({
      opNumber: opNum,
      op: operations.find(o => o.operation_number === opNum),
      items: groups[opNum],
      editable: canEditOp(opNum)
    }));
  })();

  // For multi-op WOs, only count items from visible (current + completed) operations
  const visibleTaskListItems = (() => {
    const opsWithItems = taskListByOp.filter(g => g.items.length > 0);
    if (opsWithItems.length <= 1) return taskListItems;
    const visible = [];
    for (let i = 0; i < opsWithItems.length; i++) {
      const g = opsWithItems[i];
      visible.push(...g.items);
      const hasFinal = g.op ? timeConfirmations.filter(tc => tc.operation_id === g.op.id).some(tc => tc.is_final) : false;
      const isComplete = g.op?.status === 'Complete' || hasFinal;
      if (!isComplete) break;
    }
    return visible;
  })();
  const checkedCount = visibleTaskListItems.filter(i => i.value || i.pfna || i.checked).length;
  const taskListProgress = visibleTaskListItems.length > 0 ? Math.round((checkedCount / visibleTaskListItems.length) * 100) : 0;

  // Calculate total time
  const totalMinutes = timeConfirmations.reduce((sum, tc) => sum + (tc.hours || 0) * 60 + (tc.minutes || 0), 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  // Calculate costs
  const labourCost = timeConfirmations.reduce((sum, tc) => {
    const hrs = (tc.hours || 0) + (tc.minutes || 0) / 60;
    return sum + hrs * (parseFloat(tc.hourly_rate) || 0);
  }, 0);
  // Total material cost minus any reimbursements that have been received —
  // this is the "effective" spend on parts/services after refunds.
  const materialCost = materials.reduce((sum, m) => {
    const base = m.material_type === 'labour'
      ? (m.price_type === 'per_hour' ? (parseFloat(m.price_per_hour) || 0) * (parseInt(m.quantity) || 1) : (parseFloat(m.total_price) || 0))
      : (parseFloat(m.cost) || 0);
    const refunded = Array.isArray(m.reimbursements)
      ? m.reimbursements.filter(r => r.received_date).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
      : 0;
    return sum + Math.max(base - refunded, 0);
  }, 0);
  const totalCost = labourCost + materialCost;

  if (loading) {
    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div className="skeleton" style={{ width: 24, height: 24 }} />
          <div className="skeleton" style={{ width: 90, height: 20 }} />
          <div className="skeleton" style={{ width: 60, height: 22, borderRadius: 5 }} />
          <div className="skeleton" style={{ width: 60, height: 22, borderRadius: 5 }} />
        </div>
        {/* Title */}
        <div className="skeleton" style={{ width: '50%', height: 26, marginBottom: 16 }} />
        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <div className="skeleton" style={{ width: 170, height: 40, borderRadius: 10 }} />
          <div className="skeleton" style={{ width: 140, height: 40, borderRadius: 10 }} />
        </div>
        {/* Info grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12,
          background: 'var(--bg-card)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid var(--border)'
        }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}>
              <div className="skeleton" style={{ width: 60, height: 10, marginBottom: 6 }} />
              <div className="skeleton" style={{ width: '80%', height: 16 }} />
            </div>
          ))}
        </div>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
          {[80, 90, 60, 100, 70].map((w, i) => (
            <div key={i} className="skeleton" style={{ width: w, height: 16, margin: '10px 16px' }} />
          ))}
        </div>
        {/* Content */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', height: 200 }}>
          <div className="skeleton" style={{ width: '40%', height: 14, marginBottom: 12 }} />
          <div className="skeleton" style={{ width: '70%', height: 14, marginBottom: 12 }} />
          <div className="skeleton" style={{ width: '55%', height: 14 }} />
        </div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 16, marginBottom: 8 }}>Work order not found</div>
        <div style={{ fontSize: 13, marginBottom: 16 }}>Could not load work order "{id}"</div>
        {loadError && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 16, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 6, maxWidth: 400, margin: '0 auto 16px', wordBreak: 'break-all' }}>{loadError}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={() => navigate(-1)} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Go Back
          </button>
          <button onClick={() => window.location.reload()} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isOverdue = false;
  // Anyone who can enter edit mode on the WO can also add/edit materials
  // without having to click "Edit Work Order" first. Assignee + admin +
  // supervisor mirrors the same set as the Edit Work Order button gate
  // further down the page. Materials remain editable even on TECO'd /
  // Complete WOs so supervisors can add late invoices, refunds, etc.
  // after closure without having to un-TECO first.
  const canEditWo = !!user && (
    issue.assigned_to === user.id
    || user.permissions?.includes('admin_panel')
    || user.permissions?.includes('supervisor_panel')
  );
  const materialsMutable = canEditWo;

  return (
    <div style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => fromCapacity ? navigate(`/supervisor?tab=myteam&user=${capacityUserId}`) : navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4
        }}><FiArrowLeft size={20} /></button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="selectable" style={{ fontFamily: 'monospace', fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>{issue.wo_number}{operations.length > 0 ? ` / ${operations[0].operation_number}` : ''}</span>
            <span style={{
              padding: '3px 10px', borderRadius: 5, fontSize: 12, fontWeight: 700,
              color: '#fff', background: WO_TYPE_COLORS[issue.type] || '#6b7280'
            }}>{issue.type} - {WO_TYPE_LABELS[issue.type]}</span>
            <span style={{
              padding: '3px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600,
              color: '#fff', background: statusColors[issue.status]
            }}>{issue.status}</span>
            {isOverdue && (
              <span style={{ padding: '3px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiClock size={11} /> {Math.floor((new Date() - new Date(issue.due_date)) / 86400000)}d OVERDUE
              </span>
            )}
            {issue.task_list_template_name && (
              <span style={{ padding: '3px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600, color: '#8b5cf6', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiFileText size={11} /> {issue.task_list_template_tskl && `${issue.task_list_template_tskl} — `}{issue.task_list_template_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Parent WO Link */}
      {issue.parent_wo_id && (
        <div onClick={() => { playClick(); navigate(`/work-orders/${issue.parent_wo_number}`); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 14px', borderRadius: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', cursor: 'pointer', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
          <FiGitBranch size={14} /> Sub work order of: {issue.parent_wo_number} — {issue.parent_wo_name}
        </div>
      )}

      {/* Title + Action Buttons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          {editing === 'name' && editMode ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={editValue} onChange={e => setEditValue(titleCase(e.target.value))} autoFocus
                style={{ flex: 1, fontSize: 22, fontWeight: 700, padding: '4px 8px', borderRadius: 6, border: '1px solid #2563eb', outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
              />
              <button onClick={() => handleSaveField('name', editValue)} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}><FiSave size={14} /></button>
              <button onClick={() => setEditing(null)} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: 'var(--text-secondary)' }}><FiX size={14} /></button>
            </div>
          ) : (
            <>
              <h1 className="selectable" style={{ margin: 0, fontSize: 22, fontWeight: 700, cursor: editMode ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => { if (editMode) { setEditing('name'); setEditValue(issue.name); } }}
              >
                {issue.name}
                {editMode && <FiEdit2 size={14} color="var(--text-muted)" />}
              </h1>
              {operations.length > 0 && (
                <div className="selectable" style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                  {operations.map(o => o.title).join(' / ')}
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 'auto', flexShrink: 0 }}>
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            title="Shop Paper"
            onClick={() => {
              authFetch(`/api/issues/${issue.id}/shop-paper`)
                .then(r => r.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px 10px' : '8px 12px',
              borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap'
            }}
          >
            <FiFileText size={16} />{!isMobile && ' Shop Paper'}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            title="Print"
            onClick={() => {
              authFetch(`/api/issues/${issue.id}/shop-paper`)
                .then(r => r.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  const iframe = document.createElement('iframe');
                  iframe.style.display = 'none';
                  iframe.src = url;
                  document.body.appendChild(iframe);
                  iframe.onload = () => { iframe.contentWindow.print(); setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 60000); };
                });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px 10px' : '8px 12px',
              borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6366f1', whiteSpace: 'nowrap'
            }}
          >
            <FiPrinter size={16} />{!isMobile && ' Print'}
          </motion.button>
          {issue.status === 'TECO' && (user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) && (
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => {
                setConfirmDialog({
                  title: 'UN-TECO Work Order',
                  rows: [
                    { label: 'Work Order', value: issue.wo_number || `#${issue.id}` },
                    { label: 'Action', value: 'Reverse TECO and set status back to In Progress' },
                  ],
                  confirmLabel: 'UN-TECO', confirmColor: '#f59e0b',
                  onConfirm: async () => {
                    setConfirmDialog(null);
                    const res = await authFetch(`/api/issues/${resolvedId}/un-teco`, { method: 'POST' });
                    if (res.ok) {
                      const updated = await authFetch(`/api/issues/${resolvedId}`).then(r => r.json());
                      setIssue(updated);
                      authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
                      authFetch(`/api/issues/${resolvedId}/time-confirmations`).then(r => r.ok ? r.json() : []).then(setTimeConfirmations);
                      authFetch(`/api/issues/${resolvedId}/operations`).then(r => r.ok ? r.json() : []).then(setOperations);
                      playSuccess();
                      showToast('TECO reversed — work order set to In Progress', 'success');
                    } else {
                      const data = await res.json().catch(() => ({}));
                      showToast(data.error || 'Failed to reverse TECO', 'error');
                    }
                  }
                });
              }}
              title="UN-TECO"
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px 10px' : '8px 12px',
                borderRadius: 8, border: 'none', background: '#f59e0b', color: '#fff',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(245,158,11,0.3)'
              }}
            >
              <FiRefreshCw size={16} />{!isMobile && ' UN-TECO'}
            </motion.button>
          )}
          {issue.status !== 'TECO' && (() => {
            // PM02 is template-driven (operations come from the task list),
            // so ad-hoc operations are intentionally disabled. PM01/PM03 can
            // accept new operations until the WO is TECO'd — including after
            // a Complete status, since follow-up work commonly needs to be
            // tracked against the same WO.
            const canAddOp = issue.type !== 'PM02';
            return (
            <div style={{ position: 'relative' }}>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                ref={el => { if (el) el._actionBtn = true; }}
                title="Additional Works"
                onClick={(e) => { playClick(); const rect = e.currentTarget.getBoundingClientRect(); setActionPickerPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) }); setShowActionPicker(prev => !prev); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px 10px' : '8px 12px',
                  borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.08)',
                  color: '#6366f1', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap'
                }}
              ><FiPlus size={16} />{!isMobile && ' Additional Works'}</motion.button>
              {showActionPicker && (
                <>
                  <div onClick={() => setShowActionPicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 299 }} />
                  <div style={{ position: 'fixed', top: actionPickerPos.top, right: actionPickerPos.right, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 300, width: 'min(300px, calc(100vw - 16px))', maxWidth: 300, overflow: 'hidden' }}>
                    {canAddOp && (
                    <div onClick={() => { playClick(); setShowActionPicker(false); setPendingOps([{ _id: 0, title: issue?.name || '', date: '', hours: 0, minutes: 0, description: '' }]); pendingOpIdRef.current = 1; setShowAddOpModal(true); }}
                      style={{ padding: '14px 18px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}><FiPlus size={13} />New Operation</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>Add a task or step to this work order. Sent to supervisor for approval.</div>
                    </div>
                    )}
                    <div onClick={() => { playClick(); setShowActionPicker(false); setShowSubWoModal(true); }}
                      style={{ padding: '14px 18px', cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}><FiGitBranch size={13} />Sub Work Order</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>Create a separate linked work order under this one. Use for additional scope or different teams.</div>
                    </div>
                  </div>
                </>
              )}
            </div>
            );
          })()}
          {/* Show TC button whenever any op is non-complete, even if the WO header
              status is Complete/TECO — additional ops added after a Complete need
              time confirmations. Only fully hide on TECO with no pending ops. */}
          {issue.status !== 'TECO' && operations.some(op => op.status !== 'Complete') && (
            <>
              {operations.some(op => op.status !== 'Complete') && (() => {
                const inspectionIncomplete = issue.type === 'PM02' && visibleTaskListItems.length > 0 && checkedCount < visibleTaskListItems.length;
                const disabled = inspectionIncomplete || !canConfirmTime.allowed;
                const disabledReason = inspectionIncomplete ? `Complete inspection first (${checkedCount}/${visibleTaskListItems.length})` : canConfirmTime.reason;
                if (!canConfirmTime.allowed && !user?.permissions?.includes('admin_panel') && !user?.permissions?.includes('supervisor_panel')) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                      <FiClock size={13} /> {canConfirmTime.reason}
                    </div>
                  );
                }
                return (
                <motion.button
                  whileHover={disabled ? {} : { scale: 1.02 }} whileTap={disabled ? {} : { scale: 0.98 }}
                  onClick={() => { if (!disabled) setShowTimeConfirm(true); }}
                  title={disabledReason || 'Time Confirmation'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '8px 10px' : '8px 12px',
                    borderRadius: 8, border: 'none', background: disabled ? '#94a3b8' : '#2563eb',
                    color: '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                    opacity: disabled ? 0.6 : 1,
                    boxShadow: disabled ? 'none' : '0 2px 8px rgba(37,99,235,0.3)'
                  }}
                >
                  <FiClock size={16} />{!isMobile && ' Time Confirmation'}
                </motion.button>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12,
        background: 'var(--bg-card)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid var(--border)'
      }}>
        <InfoItem label="Priority" value={issue.priority}
          color={priorityColors[issue.priority]}
        />
        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <FiUser size={11} /> Assigned To
          </div>
          {(() => {
            const directNames = operations.filter(op => op.assigned_to_name).map(op => op.assigned_to_name);
            const assigneeNames = assignees.map(a => a.user_name).filter(Boolean);
            const allNames = [...new Set([...directNames, ...assigneeNames])];
            return allNames.length > 0 ? allNames.map(name => (
              <div key={name} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{name}</div>
            )) : (
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>Unassigned</div>
            );
          })()}
        </div>
        {(issue.asset_name || editMode) && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Asset</div>
            {editing === 'asset' && editMode ? (
              <div style={{ position: 'relative' }}>
                <input value={assetSearch} autoFocus placeholder="Search assets..."
                  onChange={e => {
                    const q = e.target.value.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
                    setAssetSearch(q);
                    if (q.trim().length < 2) { setAssetResults([]); return; }
                    setAssetSearching(true);
                    authFetch(`/api/assets?search=${encodeURIComponent(q.trim())}&limit=10`).then(r => r.ok ? r.json() : { data: [] }).then(res => { setAssetResults(res.data || []); setAssetSearching(false); }).catch(() => setAssetSearching(false));
                  }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #2563eb', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                {assetResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 200, overflowY: 'auto', boxShadow: 'var(--shadow-xl)' }}>
                    {assetResults.map(a => (
                      <div key={a.id} onClick={async () => {
                        const res = await authFetch(`/api/issues/${resolvedId}`, { method: 'PUT', body: JSON.stringify({ asset_id: a.id }) });
                        if (res.ok) { const updated = await res.json(); setIssue(updated); authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory); }
                        setEditing(null); setAssetSearch(''); setAssetResults([]);
                      }}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {a.tag && <span style={{ color: '#2563eb', marginRight: 6 }}>{a.tag}</span>}
                          {a.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {assetSearching && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Searching...</div>}
                <button onClick={() => { setEditing(null); setAssetSearch(''); setAssetResults([]); }}
                  style={{ marginTop: 6, padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word', minWidth: 0 }}>{issue.asset_name || 'No asset'}</span>
                {issue.asset_id && (
                  <button onClick={() => navigate(`/assets/${issue.asset_id}`)}
                    style={{ padding: '2px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: '#2563eb', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                    <FiEye size={11} /> View
                  </button>
                )}
                {editMode && (user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) && (
                  <FiEdit2 size={12} color="var(--text-muted)" style={{ cursor: 'pointer' }} onClick={() => { setEditing('asset'); setAssetSearch(''); setAssetResults([]); }} />
                )}
              </div>
            )}
          </div>
        )}
        {issue.frequency && <InfoItem label="Frequency" value={issue.frequency} />}
        <InfoItem label="Created By" value={issue.added_by_name || 'Unknown'} />
        <InfoItem label="Created" value={formatDate(issue.created_at)} />
        {totalMinutes > 0 && (
          <InfoItem icon={FiClock} label="Planned Time" value={`${totalHours}h ${totalMins}m`} color="#2563eb" />
        )}
        {totalCost > 0 && (
          <InfoItem label="Total Cost" value={`$${totalCost.toFixed(2)}`} color="#059669" />
        )}
        {labourCost > 0 && materialCost > 0 && (
          <InfoItem label="Cost Breakdown" value={`Labour: $${labourCost.toFixed(2)} | Parts: $${materialCost.toFixed(2)}`} color="#6b7280" />
        )}
        {issue.approval_group_name && (
          <InfoItem label="Approval Group" value={issue.approval_group_name} color={issue.approval_group_color || '#6366f1'} />
        )}
        {issue.approval_status && (
          <InfoItem label="Approval" value={issue.approval_status}
            color={issue.approval_status === 'Approved' ? '#10b981' : issue.approval_status === 'Rejected' ? '#ef4444' : '#f59e0b'} />
        )}
        {issue.approved_by_name && (
          <InfoItem label={issue.approval_status === 'Approved' ? 'Approved By' : 'Reviewed By'} value={`${issue.approved_by_name} — ${formatDate(issue.approved_at)}`} />
        )}
      </div>

      {/* Approval Actions */}
      {issue.approval_status === 'Pending' && issue.approval_group_id && (
        <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #fcd34d', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 2 }}>Pending Approval</div>
            <div style={{ fontSize: 12, color: '#a16207' }}>This work order requires approval from "{issue.approval_group_name}"</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={async () => {
                const r = await authFetch(`/api/issues/${issue.id}/approve`, { method: 'PATCH', body: JSON.stringify({ action: 'approve' }) });
                if (r.ok) { playSuccess(); window.location.reload(); }
                else { const d = await r.json(); alert(d.error); }
              }}
              style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Approve
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => {
                setConfirmDialog({
                  title: 'Reject Work Order',
                  rows: [{ label: 'Work Order', value: issue.wo_number || issue.name }, { label: 'Action', value: 'This work order will be rejected' }],
                  confirmLabel: 'Reject', confirmColor: '#dc2626',
                  onConfirm: async () => {
                    setConfirmDialog(null);
                    const r = await authFetch(`/api/issues/${issue.id}/approve`, { method: 'PATCH', body: JSON.stringify({ action: 'reject' }) });
                    if (r.ok) { playDelete(); window.location.reload(); }
                    else { const d = await r.json(); alert(d.error); }
                  }
                });
              }}
              style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #ef4444', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Reject
            </motion.button>
          </div>
        </div>
      )}

      {/* Materials not ready warning — shown when WO is Complete but parts not fully done */}
      {issue.status === 'Complete' && materials.length > 0 && (() => {
        const incompleteMats = materials.filter(m => {
          if (m.material_type === 'labour') return !(m.contacted && m.labour_complete && m.paid);
          return !(m.ordered && m.delivered && m.installed);
        });
        if (incompleteMats.length === 0) return null;
        return (
          <div style={{ background: 'linear-gradient(135deg, #fff7ed 0%, #fff3e0 100%)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #fb923c', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <FiBox size={18} color="#ea580c" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>Work Order Not Ready for TECO</div>
              <div style={{ fontSize: 12, color: '#c2410c', lineHeight: 1.5 }}>
                {incompleteMats.length} part{incompleteMats.length > 1 ? 's' : ''} must be fully received before this work order can be TECO'd:{' '}
                <strong>{incompleteMats.map(m => m.part_name || m.description).join(', ')}</strong>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sub Work Orders */}
      {subWorkOrders.length > 0 && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiGitBranch size={14} color="#6366f1" /> Sub Work Orders ({subWorkOrders.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
            {subWorkOrders.map((sub, idx) => {
              const sc = statusColors[sub.status] || statusColors['New'];
              const canDeleteSub = (
                user?.permissions?.includes('admin_panel') ||
                user?.permissions?.includes('delete_work_order')
              );
              return (
                <div key={sub.id} style={{ display: 'flex', alignItems: 'stretch', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                    {idx > 0 && <div style={{ width: 2, flex: 1, background: 'var(--border)' }} />}
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                    {idx < subWorkOrders.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--border)' }} />}
                  </div>
                  <div onClick={() => { playClick(); navigate(`/work-orders/${sub.wo_number}`); }}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 4,
                      background: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--border-subtle)',
                      cursor: 'pointer', transition: 'background 0.15s', flexWrap: 'wrap'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border-subtle)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-input)'}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, minWidth: 80 }}>{sub.wo_number}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[sub.type] || '#6b7280' }}>{sub.type}</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', minWidth: 100 }}>{sub.name}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, color: '#fff', background: sc }}>{sub.status}</span>
                    {sub.approval_status && (
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        color: sub.approval_status === 'Approved' ? '#10b981' : sub.approval_status === 'Rejected' ? '#ef4444' : '#f59e0b',
                        background: sub.approval_status === 'Approved' ? 'rgba(16,185,129,0.1)' : sub.approval_status === 'Rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                      }}>{sub.approval_status}</span>
                    )}
                    {sub.assigned_to_name && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub.assigned_to_name}</span>}
                    {canDeleteSub && sub.status !== 'TECO' && (
                      <button
                        type="button"
                        title="Delete sub work order"
                        onClick={(e) => {
                          e.stopPropagation();
                          playClick();
                          setConfirmDialog({
                            title: 'Delete sub work order?',
                            rows: [
                              { label: 'Work order', value: sub.wo_number },
                              { label: 'Name', value: sub.name },
                              { label: 'Warning', value: 'This cannot be undone.' },
                            ],
                            confirmLabel: 'Delete',
                            confirmColor: '#ef4444',
                            onConfirm: async () => {
                              setConfirmDialog(null);
                              const res = await authFetch(`/api/issues/${sub.id}`, { method: 'DELETE' });
                              if (res.ok) {
                                setSubWorkOrders(prev => prev.filter(s => s.id !== sub.id));
                                playDelete();
                                showToast('Sub work order deleted', 'success');
                              } else {
                                const data = await res.json().catch(() => ({}));
                                showToast(data.error || 'Failed to delete sub work order', 'error');
                              }
                            },
                          });
                        }}
                        style={{
                          marginLeft: 'auto', padding: '4px 6px', borderRadius: 6,
                          border: '1px solid rgba(239,68,68,0.25)', background: 'transparent',
                          color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Related Work Orders (PM02 chain from same task list) */}
      {relatedWorkOrders.length > 0 && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiGitBranch size={14} color="#0ea5e9" /> Related Work Orders ({relatedWorkOrders.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
            {relatedWorkOrders.map((rwo, idx) => {
              const isCurrent = rwo.id === resolvedId;
              const sc = statusColors[rwo.status] || statusColors['New'];
              return (
                <div key={rwo.id} style={{ display: 'flex', alignItems: 'stretch', gap: 12 }}>
                  {/* Timeline line + dot */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                    {idx > 0 && <div style={{ width: 2, flex: 1, background: 'var(--border)' }} />}
                    <div style={{ width: isCurrent ? 14 : 10, height: isCurrent ? 14 : 10, borderRadius: '50%', background: isCurrent ? '#0ea5e9' : rwo.teco_status === 'completed' ? '#10b981' : sc, flexShrink: 0, border: isCurrent ? '3px solid rgba(14,165,233,0.3)' : 'none' }} />
                    {idx < relatedWorkOrders.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--border)' }} />}
                  </div>
                  {/* WO card */}
                  <div onClick={() => { if (!isCurrent) { playClick(); navigate(`/work-orders/${rwo.wo_number}`); } }}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 4,
                      background: isCurrent ? 'rgba(14,165,233,0.06)' : 'var(--bg-input)',
                      borderRadius: 8, border: `1px solid ${isCurrent ? 'rgba(14,165,233,0.3)' : 'var(--border-subtle)'}`,
                      cursor: isCurrent ? 'default' : 'pointer', transition: 'background 0.15s', flexWrap: 'wrap'
                    }}
                    onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.background = 'rgba(14,165,233,0.06)'; e.currentTarget.style.borderColor = 'rgba(14,165,233,0.3)'; } }}
                    onMouseLeave={e => { if (!isCurrent) { e.currentTarget.style.background = 'var(--bg-input)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; } }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: isCurrent ? '#0ea5e9' : 'var(--text-muted)', fontWeight: 700, minWidth: 80 }}>{rwo.wo_number}</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', minWidth: 100 }}>{rwo.name}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, color: '#fff', background: sc }}>{rwo.teco_status === 'completed' ? 'TECO' : rwo.teco_status === 'pending' ? 'Pending TECO' : rwo.status}</span>
                    {rwo.due_date && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{(() => { const d = new Date(rwo.due_date); return isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</span>}
                    {rwo.fail_count > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.1)' }}>{rwo.fail_count} Failed</span>}
                    {rwo.is_legacy_linked === 1 && <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.1)' }}>LEGACY</span>}
                    {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: '#0ea5e9', padding: '1px 6px', borderRadius: 4, background: 'rgba(14,165,233,0.1)' }}>VIEWING</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legacy WO Description & Comments — only when viewing the legacy WO itself */}
          {issue?.is_legacy_linked === 1 && (legacyWoDescription || legacyComments.length > 0) && (
            <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              {legacyWoDescription && (
                <div className="selectable" style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: legacyComments.length > 0 ? 12 : 0 }}>
                  {stripHtml(legacyWoDescription)}
                </div>
              )}
              {legacyComments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {legacyComments.map(c => (
                    <div key={c.id} style={{ display: 'flex', gap: 10, padding: 12, background: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      {c.added_by_avatar ? (
                        <img src={`/api/users/avatar/${c.added_by_avatar}?token=${token}`} alt=""
                          style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', background: c.added_by_avatar_color || 'linear-gradient(135deg, #2563eb, #3b82f6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0
                        }}>{(() => { const p = (c.added_by_name || '?').trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0][0].toUpperCase(); })()}</div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-primary)' }}>{c.added_by_name}</span>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDateTime(c.created_at)}</span>
                        </div>
                        {c.comment && <div className="selectable note-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{linkifyText(c.comment)}</div>}
                        {c.files?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                            {c.files.map(f => <MediaViewer key={f.id} file={f} token={token} />)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {(issue.description || (editMode && (issue.added_by === user?.id || user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')))) && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            Description
            {editMode && (issue.added_by === user?.id || user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) && editing !== 'description' && (
              <FiEdit2 size={12} color="var(--text-muted)" style={{ cursor: 'pointer' }} onClick={() => { setEditing('description'); setEditValue(issue.description || ''); }} />
            )}
          </h3>
          {editing === 'description' && editMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus rows={4}
                style={{ width: '100%', fontSize: 14, padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => handleSaveField('description', editValue)} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Save</button>
                <button onClick={() => setEditing(null)} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="rich-content" style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: editMode && (issue.added_by === user?.id || user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) ? 'pointer' : 'default' }}
              onClick={() => { if (editMode && (issue.added_by === user?.id || user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel'))) { setEditing('description'); setEditValue(issue.description || ''); } }}
            >{stripHtml(issue.description) || 'No description'}</div>
          )}
        </div>
      )}

      {/* Operation Requests Log */}
      {operations.filter(o => o.approval_status === 'pending').length > 0 && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiLayers size={14} /> Pending Operation Requests
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {operations.filter(o => o.approval_status === 'pending').map(op => (
              <div key={op.id} style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{op.title}</span>
                  <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'rgba(245,158,11,0.1)' }}>Pending Approval</span>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  <span>{op.created_at ? new Date(op.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</span>
                  {op.created_by_name && <span>Requested by {op.created_by_name}</span>}
                </div>
                {op.additional_info && (
                  <div className="selectable" style={{ marginTop: 6, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{op.additional_info}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--border)', flexWrap: 'wrap' }}>
        {[
          ...(issue.type === 'PM02'
            ? [{ key: 'tasklist', label: `Task List (${visibleTaskListItems.length})`, icon: FiCheckSquare }]
            : [{ key: 'comments', label: `Comments (${comments.length})`, icon: FiMessageSquare }]),
          { key: 'operations', label: `Operations (${operations.length})`, icon: FiLayers },
          { key: 'materials', label: `Materials${materials.length > 0 ? ` (${materials.length})` : ''}`, icon: FiBox },
          { key: 'files', label: `Documents (${files.length})`, icon: FiPaperclip },
          { key: 'history', label: `History (${history.length})`, icon: FiClock },
        ].filter(Boolean).map(tab => {
          const active = woTab === tab.key;
          return (
            <button key={tab.key} onClick={() => { playClick(); setWoTab(tab.key); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                border: 'none', borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
                background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? '#2563eb' : 'var(--text-secondary)', whiteSpace: 'nowrap',
                marginBottom: -2, transition: 'all 0.2s'
              }}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Operations Tab */}
      {woTab === 'operations' && (
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', marginBottom: 16 }}>
        {operations.length > 0 && (() => {
          const completedOps = operations.filter(o => o.status === 'Complete').length;
          const pct = Math.round((completedOps / operations.length) * 100);
          return (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{completedOps} of {operations.length} operations complete</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: pct === 100 ? '#10b981' : '#2563eb' }}>{pct}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#10b981' : '#2563eb', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
            </div>
          );
        })()}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {operations.map(op => {
            const opMins = timeConfirmations.filter(tc => tc.operation_id === op.id).reduce((sum, tc) => sum + (tc.hours || 0) * 60 + (tc.minutes || 0), 0);
            const opH = Math.floor(Math.abs(opMins) / 60);
            const opM = Math.abs(opMins) % 60;
            return (
            <React.Fragment key={op.id}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 8, background: op.status === 'Complete' ? 'rgba(5,150,105,0.06)' : op.status === 'In Progress' ? 'rgba(217,119,6,0.04)' : 'var(--bg-input)',
              border: `1px solid ${op.status === 'Complete' ? 'rgba(5,150,105,0.2)' : op.status === 'In Progress' ? 'rgba(217,119,6,0.3)' : 'var(--border-subtle)'}`
            }}>
              <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', minWidth: 40 }}>{op.operation_number}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editingOpId === op.id ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input value={editingOpTitle} onChange={e => setEditingOpTitle(e.target.value)} autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          authFetch(`/api/issues/${resolvedId}/operations/${op.id}`, { method: 'PUT', body: JSON.stringify({ title: editingOpTitle }) })
                            .then(r => r.ok ? r.json() : null).then(updated => { if (updated) setOperations(prev => prev.map(o => o.id === op.id ? updated : o)); setEditingOpId(null); });
                        }
                      }}
                      style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: '1px solid #2563eb', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                    />
                    <button onClick={() => {
                      authFetch(`/api/issues/${resolvedId}/operations/${op.id}`, { method: 'PUT', body: JSON.stringify({ title: editingOpTitle }) })
                        .then(r => r.ok ? r.json() : null).then(updated => { if (updated) setOperations(prev => prev.map(o => o.id === op.id ? updated : o)); setEditingOpId(null); });
                    }} style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, cursor: 'pointer' }}><FiSave size={12} /></button>
                    <button onClick={() => setEditingOpId(null)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: 11, cursor: 'pointer' }}><FiX size={12} /></button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{op.title}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, color: '#fff', background: issue.priority === 'Push' ? '#8b5cf6' : '#6b7280' }}>{issue.priority === 'Push' ? 'Push' : 'Normal'}</span>
                      {(op.planned_date || issue.due_date) && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FiCalendar size={10} /> {formatDate(op.planned_date || issue.due_date)}
                        </span>
                      )}
                      {op.estimated_hours > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FiClock size={10} /> Planned: {fmtMins(Math.round(op.estimated_hours * 60))}{opMins > 0 ? ` · Confirmed: ${fmtMins(opMins)}` : ''}
                        </span>
                      )}
                      {!op.estimated_hours && opMins > 0 && (
                        <span style={{ fontSize: 11, color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FiClock size={10} /> Confirmed: {fmtMins(opMins)}
                        </span>
                      )}
                      {op.estimated_hours > 0 && opMins > 0 && (() => {
                        const estMins = Math.round(op.estimated_hours * 60);
                        const diffMins = opMins - estMins;
                        const pct = Math.round((diffMins / estMins) * 100);
                        const vColor = pct > 20 ? '#ef4444' : pct < -20 ? '#16a34a' : '#f59e0b';
                        return (
                          <span title="Variance vs estimated time" style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: `${vColor}15`, color: vColor, border: `1px solid ${vColor}30`, whiteSpace: 'nowrap' }}>
                            {pct > 0 ? '▲' : pct < 0 ? '▼' : '='} {pct > 0 ? '+' : ''}{pct}%
                          </span>
                        );
                      })()}
                      {op.assigned_to_name && (
                        <span style={{ fontSize: 11, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                          <FiUser size={10} /> {op.assigned_to_name}
                        </span>
                      )}
                    </div>
                    {/* Assigned team for this operation */}
                    {(() => {
                      const opAssignees = assignees.filter(a => a.operation_id === op.id || (!a.operation_id && !assignees.some(x => x.operation_id === op.id && x.user_id === a.user_id)));
                      if (opAssignees.length === 0) return null;
                      return (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          {opAssignees.map(a => (
                            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 4px', borderRadius: 20, background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}>
                              <div style={{ width: 24, height: 24, borderRadius: '50%', background: a.avatar_color || '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                {(() => { const p = a.user_name.trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + '.' + p[p.length-1][0]).toUpperCase() : p[0][0].toUpperCase(); })()}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{a.user_name}</span>
                              {a.employee_id && <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 10, fontWeight: 600 }}>{a.employee_id}</span>}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
              {/* Status badge */}
              {(() => {
                const opTcs = timeConfirmations.filter(tc => tc.operation_id === op.id);
                const hasFinal = opTcs.some(tc => tc.is_final);
                const hasAny = opTcs.length > 0;
                const confLabel = hasFinal ? 'CNF' : hasAny ? 'PCNF' : 'NEW';
                const confColor = hasFinal ? '#059669' : hasAny ? '#d97706' : 'var(--text-muted)';
                const confBg = hasFinal ? 'rgba(5,150,105,0.1)' : hasAny ? 'rgba(217,119,6,0.1)' : 'var(--border-subtle)';
                return <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, color: confColor, background: confBg }}>{confLabel}</span>;
              })()}
              {op.approval_status && (
                <span style={{
                  padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600,
                  color: op.approval_status === 'approved' ? '#d97706' : op.approval_status === 'rejected' ? '#dc2626' : op.approval_status === 'info_required' ? '#f59e0b' : '#d97706',
                  background: op.approval_status === 'approved' ? 'rgba(217,119,6,0.1)' : op.approval_status === 'rejected' ? 'rgba(220,38,38,0.1)' : op.approval_status === 'info_required' ? 'rgba(245,158,11,0.1)' : 'rgba(217,119,6,0.1)'
                }}>{op.approval_status === 'pending' ? 'Awaiting Approval' : op.approval_status === 'approved' ? 'Approved' : op.approval_status === 'info_required' ? 'More Info Required' : 'Rejected'}</span>
              )}
              {op.approval_status === 'pending' && (user?.permissions?.includes('supervisor_panel') || user?.permissions?.includes('admin_panel')) && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button onClick={() => {
                    authFetch(`/api/issues/${resolvedId}/operations/${op.id}/approval`, { method: 'PUT', body: JSON.stringify({ status: 'approved' }) })
                      .then(r => r.ok ? r.json() : null).then(updated => {
                        if (updated) {
                          playSuccess();
                          setOperations(prev => prev.map(o => o.id === op.id ? updated : o));
                          authFetch(`/api/issues/${resolvedId}`).then(r => r.ok ? r.json() : null).then(d => { if (d) setIssue(d); });
                        }
                      });
                  }} style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                    Approve
                  </button>
                  <button onClick={() => {
                    authFetch(`/api/issues/${resolvedId}/operations/${op.id}/approval`, { method: 'PUT', body: JSON.stringify({ status: 'rejected' }) })
                      .then(r => r.ok ? r.json() : null).then(updated => { if (updated) { playDelete(); setOperations(prev => prev.map(o => o.id === op.id ? updated : o)); } });
                  }} style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                    Reject
                  </button>
                </div>
              )}
              {editMode && (user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) && editingOpId !== op.id && (
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  {issue.type !== 'PM02' && (
                  <button onClick={() => {
                    const hrs = op.estimated_hours ? Math.floor(op.estimated_hours) : 0;
                    const mins = op.estimated_hours ? Math.round((op.estimated_hours % 1) * 60) : 0;
                    const opUser = op.assigned_to || issue.assigned_to || '';
                    setEditOpForm({ title: op.title, planned_date: op.planned_date ? op.planned_date.substring(0, 10) : '', est_hours: hrs, est_minutes: mins, assigned_to: String(opUser) });
                    setShowEditOpModal(op);
                    setEditOpUserSearch('');
                    setEditOpUserDropdownOpen(false);
                    if (opUser) {
                      authFetch(`/api/issues/user-capacity-daily?user_id=${opUser}&weeks=${editOpCapRange}`)
                        .then(r => r.json()).then(setEditOpCapacity).catch(() => {});
                    } else {
                      setEditOpCapacity([]);
                    }
                  }}
                    style={{ padding: '3px 5px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 10 }}>
                    <FiEdit2 size={12} />
                  </button>
                  )}
                  {operations.length > 1 && (
                    <button onClick={() => {
                      authFetch(`/api/issues/${resolvedId}/operations/${op.id}`, { method: 'DELETE' })
                        .then(r => { if (r.ok) { playDelete(); setOperations(prev => prev.filter(o => o.id !== op.id)); } });
                    }}
                      style={{ padding: '3px 5px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 10 }}>
                      <FiTrash2 size={12} />
                    </button>
                  )}
                </div>
              )}
              </div>
            </div>
            {op.approval_status === 'info_required' && op.info_required_message && (
              <div style={{ marginTop: 8, padding: 12, borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>More Info Required from Supervisor</div>
                <div className="selectable" style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{op.info_required_message}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <textarea
                    placeholder="Type your response..."
                    id={`inf-response-${op.id}`}
                    rows={2}
                    onKeyDown={e => {
                      // Auto-capitalize after sentence endings
                      const ta = e.target;
                      if (ta.value.length === 0 && /[a-z]/.test(e.key) && e.key.length === 1) {
                        e.preventDefault();
                        ta.value = e.key.toUpperCase();
                      }
                    }}
                    onChange={e => {
                      let val = e.target.value;
                      if (val.length >= 3 && /[.!?]\s[a-z]$/.test(val.slice(-3))) {
                        e.target.value = val.slice(0, -1) + val.slice(-1).toUpperCase();
                      }
                    }}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
                      fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)',
                      resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box'
                    }}
                  />
                  <button onClick={() => {
                    const ta = document.getElementById(`inf-response-${op.id}`);
                    if (!ta || !ta.value.trim()) return;
                    authFetch(`/api/issues/${resolvedId}/operations/${op.id}/info-response`, {
                      method: 'PUT', body: JSON.stringify({ response: ta.value.trim() })
                    }).then(r => {
                      if (r.ok) {
                        playSuccess();
                        authFetch(`/api/issues/${resolvedId}/operations`).then(r => r.ok ? r.json() : []).then(setOperations);
                      }
                    });
                  }}
                    style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    Send Response
                  </button>
                </div>
              </div>
            )}
            {/* Time confirmations list */}
            {(() => {
              const opTcs = timeConfirmations.filter(tc => tc.operation_id === op.id && !tc.is_reversal);
              if (opTcs.length === 0) return null;
              const isSupervisor = user?.permissions?.includes('supervisor_panel') || user?.permissions?.includes('admin_panel');
              return (
                <div style={{ marginTop: 6, marginLeft: 50, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {opTcs.map(tc => {
                    const tcMins = (tc.hours || 0) * 60 + (tc.minutes || 0);
                    const canCancel = isSupervisor || tc.user_id === user?.id;
                    const hasReversal = timeConfirmations.some(r => r.is_reversal && r.hours === -(tc.hours || 0) && r.minutes === -(tc.minutes || 0) && r.operation_id === tc.operation_id);
                    return (
                      <div key={tc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', borderRadius: 6, background: hasReversal ? 'rgba(220,38,38,0.04)' : 'var(--bg-input)', border: '1px solid var(--border-subtle)', opacity: hasReversal ? 0.5 : 1 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 65 }}>{new Date(tc.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', minWidth: 80 }}>{tc.user_name || 'Unknown'}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#059669' }}>{fmtMins(tcMins)}</span>
                        {!!tc.is_final && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'rgba(5,150,105,0.1)', color: '#059669' }}>FINAL</span>}
                        {!!hasReversal && <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626' }}>CANCELLED</span>}
                        <div style={{ flex: 1 }} />
                        {canCancel && !hasReversal && (
                          <button onClick={() => {
                            setCancelTcTarget(tc.id);
                            setCancelTcReason('');
                          }}
                            style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.06)', color: '#dc2626', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            </React.Fragment>
            );
          })}
        </div>
        {operations.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: 20 }}>No operations</div>}
        {operations.length > 0 && (() => {
          const totalPlannedMins = operations.reduce((s, op) => s + Math.round((parseFloat(op.estimated_hours) || 0) * 60), 0);
          const totalConfirmedMins = operations.reduce((s, op) => s + timeConfirmations.filter(tc => tc.operation_id === op.id).reduce((ss, tc) => ss + (tc.hours || 0) * 60 + (tc.minutes || 0), 0), 0);
          if (totalPlannedMins === 0 && totalConfirmedMins === 0) return null;
          return (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {totalPlannedMins > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>WO Planned Total: <strong style={{ color: 'var(--text-primary)' }}>{fmtMins(totalPlannedMins)}</strong></span>}
              {totalConfirmedMins > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>WO Confirmed Total: <strong style={{ color: '#059669' }}>{fmtMins(totalConfirmedMins)}</strong></span>}
            </div>
          );
        })()}
      </div>
      )}

      {/* Materials Tab */}
      {woTab === 'materials' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', marginBottom: 16 }}>
          {/* Add/Edit Material Modal */}
          <AnimatePresence>
            {showMaterialModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 520, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editingMaterial ? (materialForm.material_type === 'labour' ? 'Edit External Labour' : materialForm.material_type === 'service' ? 'Edit Service / Bill' : 'Edit Part / Material') : (materialForm.material_type === 'labour' ? 'Add External Labour' : materialForm.material_type === 'service' ? 'Add Service / Bill' : 'Add Part / Material')}</h3>
                    <button onClick={() => { setShowMaterialModal(false); setEditingMaterial(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Type selector */}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Type</label>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[{ val: 'part', label: 'Part / Material' }, { val: 'service', label: 'Service / Bill' }, { val: 'labour', label: 'External Labour' }].map(opt => (
                          <button key={opt.val} onClick={() => setMaterialForm(f => ({ ...f, material_type: opt.val }))}
                            style={{ flex: 1, padding: '8px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${materialForm.material_type === opt.val ? 'var(--accent)' : 'var(--border)'}`, background: materialForm.material_type === opt.val ? 'rgba(37,99,235,0.08)' : 'transparent', color: materialForm.material_type === opt.val ? 'var(--accent)' : 'var(--text-secondary)' }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {materialForm.material_type === 'service' ? (
                      <>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Service Description *</label>
                          <input value={materialForm.part_name} onChange={e => setMaterialForm(f => ({ ...f, part_name: titleCase(e.target.value) }))}
                            placeholder="e.g. Annual Vet Consultation" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Invoice / Ref #</label>
                            <input value={materialForm.part_number} onChange={e => setMaterialForm(f => ({ ...f, part_number: e.target.value.toUpperCase() }))}
                              placeholder="e.g. INV-00123" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Amount ($)</label>
                            <input type="number" step="0.01" min="0" value={materialForm.cost} onChange={e => setMaterialForm(f => ({ ...f, cost: e.target.value }))}
                              placeholder="0.00" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Provider / Supplier</label>
                          <input value={materialForm.supplier_name} onChange={e => setMaterialForm(f => ({ ...f, supplier_name: titleCase(e.target.value) }))}
                            placeholder="e.g. Smith Veterinary Clinic" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Notes</label>
                          <textarea value={materialForm.description} onChange={e => { const val = e.target.value.replace(/(^|\.\s+)(\w)/g, (m, p1, p2) => p1 + p2.toUpperCase()); setMaterialForm(f => ({ ...f, description: val })); }} placeholder="Any notes about this service..." rows={2}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
                        </div>
                      </>
                    ) : materialForm.material_type === 'part' ? (
                      <>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Part Name *</label>
                          <input value={materialForm.part_name} onChange={e => setMaterialForm(f => ({ ...f, part_name: titleCase(e.target.value) }))}
                            placeholder="e.g. Bearing Assembly" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 12 }}>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Part Number</label>
                            <input value={materialForm.part_number} onChange={e => setMaterialForm(f => ({ ...f, part_number: e.target.value.toUpperCase() }))}
                              placeholder="e.g. BRG-2024A" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Supplier Name</label>
                            <input value={materialForm.supplier_name} onChange={e => setMaterialForm(f => ({ ...f, supplier_name: titleCase(e.target.value) }))}
                              placeholder="e.g. Parts Direct" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Qty</label>
                            <input type="number" min="1" value={materialForm.quantity} onChange={e => setMaterialForm(f => ({ ...f, quantity: e.target.value }))}
                              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
                          <textarea value={materialForm.description} onChange={e => {
                            const val = e.target.value.replace(/(^|\.\s+)(\w)/g, (m, p1, p2) => p1 + p2.toUpperCase());
                            setMaterialForm(f => ({ ...f, description: val }));
                          }} placeholder="Optional description..." rows={2}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Warranty</label>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <input type="number" min="0" value={materialForm.warranty_value} onChange={e => setMaterialForm(f => ({ ...f, warranty_value: e.target.value }))}
                                placeholder="0" style={{ width: '50%', padding: '10px 8px', borderRadius: '8px 0 0 8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                              <select value={materialForm.warranty_unit} onChange={e => setMaterialForm(f => ({ ...f, warranty_unit: e.target.value }))}
                                style={{ width: '50%', padding: '10px 4px', borderRadius: '0 8px 8px 0', border: '1px solid var(--border)', borderLeft: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Cost ($)</label>
                            <input type="number" step="0.01" min="0" value={materialForm.cost} onChange={e => setMaterialForm(f => ({ ...f, cost: e.target.value }))}
                              placeholder="0.00" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Storage Location</label>
                            <input value={materialForm.storage_location} onChange={e => setMaterialForm(f => ({ ...f, storage_location: titleCase(e.target.value) }))}
                              placeholder="e.g. Shelf B3" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Tracking Supplier</label>
                            <input value={materialForm.tracking_supplier} onChange={e => setMaterialForm(f => ({ ...f, tracking_supplier: titleCase(e.target.value) }))}
                              placeholder="e.g. Australia Post" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Tracking Number</label>
                            <input value={materialForm.tracking_number} onChange={e => setMaterialForm(f => ({ ...f, tracking_number: e.target.value.toUpperCase() }))}
                              placeholder="e.g. AU123456789" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Labour Description *</label>
                          <input value={materialForm.part_name} onChange={e => setMaterialForm(f => ({ ...f, part_name: titleCase(e.target.value) }))}
                            placeholder="e.g. Electrical Contractor" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 12 }}>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Company Name</label>
                            <input value={materialForm.company_name} onChange={e => setMaterialForm(f => ({ ...f, company_name: titleCase(e.target.value) }))}
                              placeholder="e.g. Smith Electrical" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Qty</label>
                            <input type="number" min="1" value={materialForm.quantity} onChange={e => setMaterialForm(f => ({ ...f, quantity: e.target.value }))}
                              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
                          <textarea value={materialForm.description} onChange={e => {
                            const val = e.target.value.replace(/(^|\.\s+)(\w)/g, (m, p1, p2) => p1 + p2.toUpperCase());
                            setMaterialForm(f => ({ ...f, description: val }));
                          }} placeholder="Scope of work..." rows={2}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Pricing</label>
                          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                            {[{ val: 'per_hour', label: 'Per Hour' }, { val: 'total', label: 'Total Price' }].map(opt => (
                              <button key={opt.val} onClick={() => setMaterialForm(f => ({ ...f, price_type: opt.val }))}
                                style={{ padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${materialForm.price_type === opt.val ? 'var(--accent)' : 'var(--border)'}`, background: materialForm.price_type === opt.val ? 'rgba(37,99,235,0.08)' : 'transparent', color: materialForm.price_type === opt.val ? 'var(--accent)' : 'var(--text-secondary)' }}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {materialForm.price_type === 'per_hour' ? (
                            <input type="number" step="0.01" min="0" value={materialForm.price_per_hour} onChange={e => setMaterialForm(f => ({ ...f, price_per_hour: e.target.value }))}
                              placeholder="Price per hour ($)" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          ) : (
                            <input type="number" step="0.01" min="0" value={materialForm.total_price} onChange={e => setMaterialForm(f => ({ ...f, total_price: e.target.value }))}
                              placeholder="Total price ($)" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          )}
                        </div>
                      </>
                    )}

                    <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg-card)', paddingTop: 12, marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
                      <button onClick={() => { setShowMaterialModal(false); setEditingMaterial(null); }}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                      <button disabled={!materialForm.part_name.trim()} onClick={async () => {
                        const payload = { ...materialForm, quantity: materialForm.quantity ? parseInt(materialForm.quantity) : 1, cost: materialForm.cost ? parseFloat(materialForm.cost) : null, warranty_value: materialForm.warranty_value ? parseInt(materialForm.warranty_value) : null, price_per_hour: materialForm.price_per_hour ? parseFloat(materialForm.price_per_hour) : null, total_price: materialForm.total_price ? parseFloat(materialForm.total_price) : null };
                        if (editingMaterial) {
                          const res = await authFetch(`/api/issues/${resolvedId}/materials/${editingMaterial.id}`, { method: 'PUT', body: JSON.stringify(payload) });
                          if (res.ok) {
                            playSuccess();
                            setMaterials(prev => prev.map(m => m.id === editingMaterial.id ? { ...m, ...payload } : m));
                            authFetch(`/api/issues/${resolvedId}/materials`).then(r => r.ok ? r.json() : []).then(setMaterials);
                          }
                        } else {
                          const res = await authFetch(`/api/issues/${resolvedId}/materials`, { method: 'POST', body: JSON.stringify(payload) });
                          if (res.ok) { const mat = await res.json(); playSuccess(); setMaterials(prev => [...prev, mat]); }
                        }
                        authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
                        setShowMaterialModal(false); setEditingMaterial(null);
                      }}
                        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !materialForm.part_name.trim() ? '#94a3b8' : 'var(--accent)', color: '#fff', cursor: !materialForm.part_name.trim() ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {editingMaterial ? 'Update' : (materialForm.material_type === 'labour' ? 'Add Labour' : materialForm.material_type === 'service' ? 'Add Service' : 'Add Part')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{(() => {
              const parts = materials.filter(m => m.material_type === 'part').length;
              const services = materials.filter(m => m.material_type === 'service').length;
              const labour = materials.filter(m => m.material_type === 'labour').length;
              const labels = [];
              if (parts > 0) labels.push(`${parts} part${parts !== 1 ? 's' : ''}`);
              if (services > 0) labels.push(`${services} service${services !== 1 ? 's' : ''}`);
              if (labour > 0) labels.push(`${labour} labour`);
              if (labels.length === 0) return 'No materials';
              const total = materials.reduce((s, m) => {
                const base = m.material_type === 'labour'
                  ? (m.price_type === 'per_hour' ? (parseFloat(m.price_per_hour) || 0) * (parseInt(m.quantity) || 1) : (parseFloat(m.total_price) || 0))
                  : (parseFloat(m.cost) || 0);
                const refunded = Array.isArray(m.reimbursements)
                  ? m.reimbursements.filter(r => r.received_date).reduce((sr, r) => sr + (parseFloat(r.amount) || 0), 0)
                  : 0;
                return s + Math.max(base - refunded, 0);
              }, 0);
              return `${labels.join(', ')}${total > 0 ? ` — Total: $${total.toFixed(2)}` : ''}`;
            })()}</div>
            {materialsMutable && (
              <button onClick={() => { setMaterialForm({ material_type: 'part', part_name: '', part_number: '', quantity: 1, supplier_name: '', description: '', warranty_value: '', warranty_unit: 'months', cost: '', storage_location: '', company_name: '', price_type: 'total', price_per_hour: '', total_price: '', tracking_supplier: '', tracking_number: '' }); setEditingMaterial(null); setShowMaterialModal(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                <FiPlus size={13} /> Add Material
              </button>
            )}
          </div>

          {materials.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <FiBox size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
              <div style={{ fontSize: 13 }}>No materials added yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {materials.map(mat => {
                const isLabour = mat.material_type === 'labour';
                const isService = mat.material_type === 'service';
                const hasProgress = isLabour ? (mat.contacted || mat.labour_complete || mat.paid) : (mat.ordered || mat.delivered || mat.installed || mat.return_packaged || mat.return_posted || mat.return_received || mat.return_refunded || mat.return_new_part_sent);
                const isComplete = isLabour ? (mat.contacted && mat.labour_complete && mat.paid) : (mat.ordered && mat.delivered && mat.installed);
                const isReturnComplete = isLabour || isService ? true : (mat.return_type === 'return' ? (mat.return_packaged && mat.return_posted && mat.return_received && mat.return_refunded) :
                  mat.return_type === 'warranty' ? (mat.return_packaged && mat.return_posted && mat.return_received && (mat.return_refunded || mat.return_new_part_sent)) : true);
                const allDone = isComplete && isReturnComplete;

                const toggleStatus = async (field, date, po_number, explicitValue) => {
                  const newValue = explicitValue !== undefined ? explicitValue : !mat[field];
                  const body = { field, value: newValue, date: newValue ? (date || null) : null };
                  if (field === 'ordered' && po_number !== undefined) body.po_number = po_number;
                  const res = await authFetch(`/api/issues/${resolvedId}/materials/${mat.id}/status`, {
                    method: 'PATCH',
                    body: JSON.stringify(body)
                  });
                  if (res.ok) {
                    const updated = await res.json();
                    setMaterials(prev => prev.map(m => m.id === mat.id ? { ...m, ...updated } : m));
                    authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
                  }
                };

                const setReturnType = async (type) => {
                  const res = await authFetch(`/api/issues/${resolvedId}/materials/${mat.id}/return`, { method: 'PATCH', body: JSON.stringify({ return_type: type }) });
                  if (res.ok) {
                    const updated = await res.json();
                    setMaterials(prev => prev.map(m => m.id === mat.id ? { ...m, ...updated } : m));
                    authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
                  }
                };

                const deleteMat = async () => {
                  let reason = '';
                  if (hasProgress) {
                    reason = prompt(`This part has progress. Enter a reason for deleting "${mat.part_name}":`);
                    if (reason === null) return; // cancelled
                    reason = reason.trim().replace(/(^|\.\s+)(\w)/g, (m, p, c) => p + c.toUpperCase());
                    if (!reason) { alert('A reason is required to delete a part with progress.'); return; }
                  } else {
                    if (!window.confirm(`Delete "${mat.part_name}"?`)) return;
                  }
                  const res = await authFetch(`/api/issues/${resolvedId}/materials/${mat.id}`, { method: 'DELETE', body: JSON.stringify({ reason }) });
                  if (res.ok) {
                    setMaterials(prev => prev.filter(m => m.id !== mat.id));
                    authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
                    playSuccess();
                  } else {
                    const d = await res.json().catch(() => ({}));
                    alert(d.error || 'Cannot delete');
                  }
                };

                const today = new Date().toISOString().split('T')[0];
                const CheckboxItem = ({ label, checked, field, dateValue }) => {
                  const isPending = datePickerPending?.matId === mat.id && datePickerPending?.field === field;
                  const isChecked = !!checked && checked !== 0 && checked !== '0';
                  const validDate = dateValue && dateValue !== 0 && dateValue !== '0' && dateValue !== '0000-00-00' ? dateValue : null;
                  const handleChange = () => {
                    // Toggle local pending state — does NOT save until ✓ confirmed
                    setDatePickerPending({ matId: mat.id, field, date: validDate || today, po_number: mat.po_number || '', toUncheck: isChecked });
                  };
                  // Determine displayed checked state — pending overrides actual until confirmed
                  const displayChecked = isPending ? !datePickerPending.toUncheck : isChecked;
                  return (
                    <div style={{ padding: '3px 0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
                        <input type="checkbox" checked={displayChecked} onChange={handleChange}
                          style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
                        <span style={{ fontWeight: 600, color: displayChecked ? '#16a34a' : 'var(--text-secondary)' }}>{label}</span>
                        {isChecked && validDate ? (
                          <span style={{ fontSize: 11, color: '#16a34a', background: 'rgba(22,163,74,0.1)', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                            {formatDate(validDate)}
                          </span>
                        ) : null}
                      </label>
                      {isPending && (
                        <div style={{ marginLeft: 23, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {datePickerPending.toUncheck ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>Uncheck this status?</span>
                              <button
                                onClick={() => {
                                  toggleStatus(field, null, undefined, false);
                                  setDatePickerPending(null);
                                  playCheckSound();
                                }}
                                style={{ padding: '3px 10px', borderRadius: 6, background: '#d97706', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>
                                Confirm
                              </button>
                              <button
                                onClick={() => setDatePickerPending(null)}
                                style={{ padding: '3px 8px', borderRadius: 6, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input
                                  type="date"
                                  max={today}
                                  value={datePickerPending.date}
                                  onChange={e => setDatePickerPending(prev => ({ ...prev, date: e.target.value }))}
                                  style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}
                                />
                                <button
                                  onClick={() => {
                                    toggleStatus(field, datePickerPending.date, datePickerPending.po_number, true);
                                    setDatePickerPending(null);
                                    playCheckSound();
                                  }}
                                  style={{ padding: '3px 10px', borderRadius: 6, background: '#16a34a', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>
                                  ✓ Save
                                </button>
                                <button
                                  onClick={() => setDatePickerPending(null)}
                                  style={{ padding: '3px 8px', borderRadius: 6, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>
                                  ✕
                                </button>
                              </div>
                              {field === 'ordered' && (
                                <input
                                  type="text"
                                  placeholder="PO / Reference # (optional)"
                                  value={datePickerPending.po_number || ''}
                                  onChange={e => setDatePickerPending(prev => ({ ...prev, po_number: e.target.value }))}
                                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11, width: 200 }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div key={mat.id} style={{
                    padding: '14px 16px', borderRadius: 10, border: `1px solid ${allDone ? 'rgba(22,163,74,0.3)' : 'var(--border)'}`,
                    borderLeft: `3px solid ${allDone ? '#16a34a' : isComplete ? '#2563eb' : '#f59e0b'}`,
                    background: allDone ? 'rgba(22,163,74,0.02)' : 'var(--bg-card)'
                  }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      {isLabour && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(168,85,247,0.1)', color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Labour</span>}
                      {isService && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.1)', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Service</span>}
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{mat.part_name}</span>
                      {mat.quantity > 1 && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(37,99,235,0.1)', color: '#2563eb' }}>x{mat.quantity}</span>}
                      {!isLabour && mat.part_number && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-input)', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{mat.part_number}</span>}
                      {(() => {
                        // Apply received-reimbursements against the displayed cost.
                        // When fully refunded, show $0.00 struck through alongside the original.
                        const baseCost = parseFloat(isLabour ? mat.total_price : mat.cost) || 0;
                        const refunded = Array.isArray(mat.reimbursements)
                          ? mat.reimbursements.filter(r => r.received_date).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
                          : 0;
                        const effective = Math.max(baseCost - refunded, 0);
                        const isFullyRefunded = baseCost > 0 && effective === 0 && refunded > 0;
                        if (!isLabour) {
                          if (mat.cost == null || baseCost <= 0) return null;
                          return (
                            <span style={{ fontSize: 12, fontWeight: 700, color: isFullyRefunded ? '#16a34a' : '#059669', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {isFullyRefunded && <span style={{ textDecoration: 'line-through', opacity: 0.55, color: 'var(--text-muted)' }}>${baseCost.toFixed(2)}</span>}
                              ${effective.toFixed(2)}
                            </span>
                          );
                        }
                        // Labour: per-hour OR total price
                        if (mat.price_type === 'per_hour') {
                          if (mat.price_per_hour == null || parseFloat(mat.price_per_hour) <= 0) return null;
                          return <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>${parseFloat(mat.price_per_hour).toFixed(2)}/hr</span>;
                        }
                        if (mat.total_price == null || baseCost <= 0) return null;
                        return (
                          <span style={{ fontSize: 12, fontWeight: 700, color: isFullyRefunded ? '#16a34a' : '#059669', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {isFullyRefunded && <span style={{ textDecoration: 'line-through', opacity: 0.55, color: 'var(--text-muted)' }}>${baseCost.toFixed(2)}</span>}
                            ${effective.toFixed(2)}
                          </span>
                        );
                      })()}
                      {materialsMutable && (
                        <>
                          <button title="Edit material" onClick={() => { setEditingMaterial(mat); setMaterialForm({ material_type: mat.material_type || 'part', part_name: mat.part_name, part_number: mat.part_number || '', quantity: mat.quantity || 1, supplier_name: mat.supplier_name || '', description: mat.description || '', warranty_value: mat.warranty_value || '', warranty_unit: mat.warranty_unit || 'months', cost: mat.cost != null ? mat.cost : '', storage_location: mat.storage_location || '', company_name: mat.company_name || '', price_type: mat.price_type || 'total', price_per_hour: mat.price_per_hour != null ? mat.price_per_hour : '', total_price: mat.total_price != null ? mat.total_price : '', tracking_supplier: mat.tracking_supplier || '', tracking_number: mat.tracking_number || '' }); setShowMaterialModal(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}><FiEdit2 size={13} /></button>
                          <button title="Delete material" onClick={deleteMat} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}><FiTrash2 size={13} /></button>
                        </>
                      )}
                    </div>

                    {/* Details row */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                      {isLabour && mat.company_name && <span>Company: <strong style={{ color: 'var(--text-secondary)' }}>{mat.company_name}</strong></span>}
                      {isService && mat.supplier_name && <span>Provider: <strong style={{ color: 'var(--text-secondary)' }}>{mat.supplier_name}</strong></span>}
                      {isService && mat.part_number && <span>Ref #: <strong style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{mat.part_number}</strong></span>}
                      {!isLabour && !isService && mat.supplier_name && <span>Supplier: <strong style={{ color: 'var(--text-secondary)' }}>{mat.supplier_name}</strong></span>}
                      {!isLabour && !isService && mat.warranty_value > 0 && <span>Warranty: <strong style={{ color: 'var(--text-secondary)' }}>{mat.warranty_value} {mat.warranty_unit}</strong></span>}
                      {!isLabour && !isService && mat.storage_location && <span>Storage: <strong style={{ color: 'var(--text-secondary)' }}>{mat.storage_location}</strong></span>}
                      {!isLabour && !isService && mat.tracking_supplier && <span>Tracking Supplier: <strong style={{ color: 'var(--text-secondary)' }}>{mat.tracking_supplier}</strong></span>}
                      {!isLabour && !isService && mat.tracking_number && (() => {
                        const trackUrl = getTrackingUrl(mat.tracking_supplier, mat.tracking_number);
                        return <span>Tracking #: {trackUrl ? <a href={trackUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontFamily: 'monospace', fontWeight: 700, textDecoration: 'underline' }}>{mat.tracking_number}</a> : <strong style={{ color: '#2563eb', fontFamily: 'monospace' }}>{mat.tracking_number}</strong>}</span>;
                      })()}
                      {!isLabour && !isService && !mat.tracking_number && issue.status !== 'Complete' && issue.status !== 'TECO' && (
                        <button
                          type="button"
                          onClick={() => { setEditingMaterial(mat); setMaterialForm({ material_type: mat.material_type || 'part', part_name: mat.part_name, part_number: mat.part_number || '', quantity: mat.quantity || 1, supplier_name: mat.supplier_name || '', description: mat.description || '', warranty_value: mat.warranty_value || '', warranty_unit: mat.warranty_unit || 'months', cost: mat.cost != null ? mat.cost : '', storage_location: mat.storage_location || '', company_name: mat.company_name || '', price_type: mat.price_type || 'total', price_per_hour: mat.price_per_hour != null ? mat.price_per_hour : '', total_price: mat.total_price != null ? mat.total_price : '', tracking_supplier: mat.tracking_supplier || '', tracking_number: mat.tracking_number || '' }); setShowMaterialModal(true); }}
                          style={{ background: 'rgba(37,99,235,0.08)', border: '1px dashed rgba(37,99,235,0.35)', color: '#2563eb', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title="Add tracking number">
                          <FiPlus size={11} /> Add tracking
                        </button>
                      )}
                    </div>
                    {mat.description && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.4, padding: '6px 10px', background: 'var(--bg-input)', borderRadius: 6 }}>{mat.description}</div>}

                    {/* Status checkboxes */}
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                      {isLabour ? (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Progress</div>
                          <CheckboxItem label="Contacted" checked={mat.contacted} field="contacted" dateValue={mat.contacted_date} />
                          <CheckboxItem label="Labour Complete" checked={mat.labour_complete} field="labour_complete" dateValue={mat.labour_complete_date} />
                          <CheckboxItem label="Paid" checked={mat.paid} field="paid" dateValue={mat.paid_date} />
                        </div>
                      ) : isService ? (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Progress</div>
                          <CheckboxItem label="Invoice Received" checked={mat.ordered} field="ordered" dateValue={mat.ordered_date} />
                          <CheckboxItem label="Approved" checked={mat.delivered} field="delivered" dateValue={mat.delivered_date} />
                          <CheckboxItem label="Paid" checked={mat.installed} field="installed" dateValue={mat.installed_date} />
                        </div>
                      ) : (
                        <>
                          {(() => {
                            const baseC = parseFloat(mat.cost) || 0;
                            const refundedR = Array.isArray(mat.reimbursements)
                              ? mat.reimbursements.filter(r => r.received_date).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
                              : 0;
                            const fullyRefunded = baseC > 0 && refundedR >= baseC;
                            if (fullyRefunded) {
                              return (
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Fulfilment</div>
                                  <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, padding: '6px 10px', borderRadius: 6, background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <FiCheck size={12} /> Waived — fully refunded
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Fulfilment</div>
                                <CheckboxItem label="Ordered" checked={mat.ordered} field="ordered" dateValue={mat.ordered_date} />
                                <CheckboxItem label="Delivered" checked={mat.delivered} field="delivered" dateValue={mat.delivered_date} />
                                <CheckboxItem label="Installed" checked={mat.installed} field="installed" dateValue={mat.installed_date} />
                              </div>
                            );
                          })()}

                          {/* Return options */}
                          {isComplete && (
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Return</div>
                              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                                {[{ val: 'none', label: 'None' }, { val: 'return', label: 'Return' }, { val: 'warranty', label: 'Warranty' }].map(opt => (
                                  <button key={opt.val} onClick={() => setReturnType(opt.val)}
                                    style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${mat.return_type === opt.val ? 'var(--accent)' : 'var(--border)'}`, background: mat.return_type === opt.val ? 'rgba(37,99,235,0.08)' : 'transparent', color: mat.return_type === opt.val ? 'var(--accent)' : 'var(--text-secondary)' }}>
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                              {mat.return_type === 'return' && (
                                <div>
                                  <CheckboxItem label="Package Item" checked={mat.return_packaged} field="return_packaged" dateValue={mat.return_packaged_date} />
                                  <CheckboxItem label="Post Item" checked={mat.return_posted} field="return_posted" dateValue={mat.return_posted_date} />
                                  <CheckboxItem label="Item Received" checked={mat.return_received} field="return_received" dateValue={mat.return_received_date} />
                                  <CheckboxItem label="Refund Received" checked={mat.return_refunded} field="return_refunded" dateValue={mat.return_refunded_date} />
                                </div>
                              )}
                              {mat.return_type === 'warranty' && (
                                <div>
                                  <CheckboxItem label="Package Item" checked={mat.return_packaged} field="return_packaged" dateValue={mat.return_packaged_date} />
                                  <CheckboxItem label="Post Item" checked={mat.return_posted} field="return_posted" dateValue={mat.return_posted_date} />
                                  <CheckboxItem label="Item Received" checked={mat.return_received} field="return_received" dateValue={mat.return_received_date} />
                                  <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>Resolution</div>
                                  <CheckboxItem label="Refund Received" checked={mat.return_refunded} field="return_refunded" dateValue={mat.return_refunded_date} />
                                  <CheckboxItem label="New Part Sent" checked={mat.return_new_part_sent} field="return_new_part_sent" dateValue={mat.return_new_part_sent_date} />
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Partial reimbursement claims (refunds against this material) */}
                    <MaterialReimbursements
                      material={mat}
                      authFetch={authFetch}
                      onChange={(updated) => setMaterials(prev => prev.map(m => m.id === updated.id ? updated : m))}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Files Tab */}
      {woTab === 'files' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            {files.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {files.map((f) => {
                  const canDelete = (f.added_by === user?.id || user?.permissions?.includes('admin_panel') || user?.permissions?.includes('manage_work_orders'));
                  const label = (f.description && f.description.trim()) || f.original_name || f.file;
                  return (
                    <div key={f.id} style={{ position: 'relative' }}>
                      <MediaViewer file={f} token={token} />
                      {editingFileId === f.id ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 6 }}>
                          <input value={editingFileDesc} onChange={e => setEditingFileDesc(e.target.value)}
                            placeholder="Leave blank to use filename"
                            style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }} />
                          <button onClick={() => handleEditFileDesc(f.id)} style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, cursor: 'pointer' }}>Save</button>
                          <button onClick={() => setEditingFileId(null)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          <div title={label} style={{ marginTop: 6, fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                          {issue.status !== 'TECO' && (
                            <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 3 }}>
                              <button onClick={() => { setEditingFileId(f.id); setEditingFileDesc(f.description || ''); }}
                                style={{ padding: '4px 6px', borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.6)', cursor: 'pointer', color: '#fff', backdropFilter: 'blur(4px)' }}
                                title="Edit description"><FiEdit2 size={12} /></button>
                              {canDelete && (
                                <button onClick={() => handleDeleteFile(f.id)}
                                  style={{ padding: '4px 6px', borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.6)', cursor: 'pointer', color: '#ef4444', backdropFilter: 'blur(4px)' }}
                                  title="Delete"><FiTrash2 size={12} /></button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>No documents attached</div>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
              onChange={e => { const files = Array.from(e.target.files); setUploadFiles(files); setUploadDescriptions(files.map(() => '')); setShowUploadMenu(false); }}
            />
            {/* Camera-only input so mobile devices jump straight to the
                camera rather than the file picker. Desktop browsers ignore
                the capture attribute and just show the picker. */}
            <input type="file" ref={cameraInputRef} style={{ display: 'none' }}
              accept="image/*" capture="environment"
              onChange={e => { const files = Array.from(e.target.files); if (files.length) { setUploadFiles(prev => [...prev, ...files]); setUploadDescriptions(prev => [...prev, ...files.map(() => '')]); } e.target.value = ''; setShowUploadMenu(false); }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              onDragOver={e => { e.preventDefault(); setFileDragging(true); }}
              onDragLeave={e => { e.preventDefault(); setFileDragging(false); }}
              onDrop={e => { e.preventDefault(); setFileDragging(false); if (e.dataTransfer.files.length) { const files = Array.from(e.dataTransfer.files); setUploadFiles(files); setUploadDescriptions(files.map(() => '')); } }}
            >
              {/* Single 'Browse files' area. Tapping it opens a small menu
                  with 'Take photo' (fires the camera input — capture hint
                  is honoured by the WebView and mobile browsers) and
                  'Select from device' (standard picker / drag-and-drop). */}
              <div style={{ position: 'relative' }}>
                <div onClick={() => setShowUploadMenu(v => !v)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px',
                  borderRadius: 8, border: `2px dashed ${fileDragging ? '#2563eb' : 'var(--border)'}`,
                  background: fileDragging ? 'rgba(37,99,235,0.06)' : 'var(--bg-input)',
                  cursor: 'pointer', fontSize: 13, color: fileDragging ? '#2563eb' : 'var(--text-secondary)',
                  transition: 'all 0.2s'
                }}>
                  <FiUpload size={16} />
                  {uploadFiles.length > 0 && !uploading
                    ? <span>{uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected</span>
                    : <span>{fileDragging ? 'Drop files here' : 'Browse files'}</span>
                  }
                </div>
                {showUploadMenu && (
                  <>
                    <div onClick={() => setShowUploadMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 40, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                      <button type="button" onClick={() => { setShowUploadMenu(false); cameraInputRef.current?.click(); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left' }}>
                        <FiCamera size={16} color="#2563eb" /> Take photo
                      </button>
                      <div style={{ height: 1, background: 'var(--border)' }} />
                      <button type="button" onClick={() => { setShowUploadMenu(false); fileInputRef.current?.click(); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left' }}>
                        <FiUpload size={16} color="#2563eb" /> Select from device
                      </button>
                    </div>
                  </>
                )}
              </div>
              {uploadFiles.length > 0 && (
                <>
                  {uploadFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                      <input
                        type="text"
                        placeholder="Description (optional — defaults to filename)"
                        value={uploadDescriptions[i] || ''}
                        onChange={e => { const v = titleCase(e.target.value); setUploadDescriptions(prev => { const n = [...prev]; n[i] = v; return n; }); }}
                        style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                  <button onClick={handleFileUpload} disabled={uploading} style={{
                    padding: '8px 14px', borderRadius: 6, border: 'none', alignSelf: 'flex-start',
                    background: uploading ? '#94a3b8' : '#2563eb',
                    color: '#fff', cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: 13, fontWeight: 600
                  }}>{uploading ? `Uploading… ${uploadProgress}%` : `Upload ${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''}`}</button>
                </>
              )}
            </div>
            {uploading && <UploadProgress files={uploadFiles} progress={uploadProgress} uploading={true} />}
          </div>
        </div>
      )}

      {/* Assignees Section removed - users shown inline per operation */}

      {/* Task List Tab - PM02 only */}
      {woTab === 'tasklist' && issue.type === 'PM02' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 16, overflow: 'hidden' }}>
          {/* Apply template (admin, edit mode, no items yet) */}
          {editMode && user?.permissions?.includes('admin_panel') && taskListItems.length === 0 && (
            <div style={{ padding: 16, background: 'rgba(37,99,235,0.04)', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', marginBottom: 8 }}>Apply Task List Template</div>
              <select defaultValue="" onChange={e => { if (e.target.value) handleApplyTemplate(parseInt(e.target.value)); }}
                onFocus={() => { if (taskTemplates.length === 0) loadTaskTemplates(); }}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minWidth: 250 }}>
                <option value="">Select a template...</option>
                {taskTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.item_count} items)</option>)}
              </select>
            </div>
          )}

          {/* Inspection status banner */}
          {taskListItems.length > 0 && hasFinalConfirmation && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>Inspection Complete</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{checkedCount} of {visibleTaskListItems.length} items ({taskListProgress}%)</span>
              </div>
            </div>
          )}

          {/* Progress bar */}
          {taskListItems.length > 0 && (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{checkedCount} of {visibleTaskListItems.length} complete</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: taskListProgress === 100 ? '#10b981' : '#2563eb' }}>{taskListProgress}%</span>
              </div>
              <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${taskListProgress}%` }} transition={{ duration: 0.4 }}
                  style={{ height: '100%', background: taskListProgress === 100 ? '#10b981' : '#2563eb', borderRadius: 2 }} />
              </div>
            </div>
          )}

          {/* Operations checklist — sequential: show current + completed ops only */}
          {taskListByOp.length > 0 && (() => {
            const opsWithItems = taskListByOp.filter(g => g.items.length > 0);
            // Sequential: only show up to and including the first non-complete operation
            // Each op is visible if all previous ops are complete (have final time confirmation)
            const visibleOps = [];
            for (let i = 0; i < opsWithItems.length; i++) {
              const g = opsWithItems[i];
              visibleOps.push(g);
              // Stop after the first non-complete operation (this is the current one)
              const hasFinal = g.op ? timeConfirmations.filter(tc => tc.operation_id === g.op.id).some(tc => tc.is_final) : false;
              const isComplete = g.op?.status === 'Complete' || hasFinal;
              if (!isComplete) break;
            }
            return visibleOps.map((group, gi) => {
            // Only editable if operation is not Complete and has no final time confirmation
            const opHasFinal = group.op ? timeConfirmations.filter(tc => tc.operation_id === group.op.id).some(tc => tc.is_final) : false;
            const opComplete = group.op?.status === 'Complete' || opHasFinal;
            const opEditable = group.editable && !opComplete;
            const opTitle = group.op ? (group.op.title || 'Operation') : (group.opNumber === '_none' ? 'General' : 'Operation');
            const opDone = group.items.filter(i => i.value || i.pfna || i.checked).length;
            const opAllDone = opDone === group.items.length && group.items.length > 0;
            return (
            <div key={gi}>
              {/* Operation header */}
              <div style={{ padding: '10px 16px', background: 'var(--bg-input)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {opAllDone && <FiCheck size={14} style={{ color: '#10b981' }} />}
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{group.opNumber !== '_none' ? `${group.opNumber} — ` : ''}{opTitle}</span>
                  {group.op?.assigned_to_name && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· {group.op.assigned_to_name}</span>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: opAllDone ? '#10b981' : 'var(--text-muted)' }}>
                  {opDone}/{group.items.length}
                </span>
              </div>

              {/* Table header — hidden on mobile */}
              {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-input)', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <span style={{ width: 36 }}>#</span>
                <span style={{ flex: 1 }}>Task</span>
                <span style={{ width: 56, textAlign: 'center' }}>Pass</span>
                <span style={{ width: 56, textAlign: 'center' }}>Fail</span>
                <span style={{ width: 56, textAlign: 'center' }}>N/A</span>
                <span style={{ flex: 1, paddingLeft: 12 }}>Comment</span>
              </div>
              )}

              {/* Items — always visible. Write handlers internally no-op when
                  canEdit is false, so we don't disable pointer events on the
                  wrapper (which would also block viewing attached photos). */}
              <div style={{ opacity: opEditable ? 1 : 0.85 }}>
              {group.items.map((item, idx) => {
            const prevSection = idx > 0 ? group.items[idx - 1].section : null;
            const showSectionHeader = item.section && item.section !== prevSection;
            const pfnaVal = item.item_type === 'checkbox' ? item.value : (item.pfna || '');
            const canEdit = opEditable;
            const handlePfna = (opt) => {
              if (!canEdit) return;
              const newVal = pfnaVal === opt ? '' : opt;
              if (newVal) playCheckSound();
              if (item.item_type === 'checkbox') {
                setTaskListItems(prev => prev.map(i => i.id === item.id ? { ...i, value: newVal, checked: newVal === 'Pass' ? 1 : 0, checked_by_name: newVal ? (user?.name || '') : null, checked_at: newVal ? new Date().toISOString() : null } : i));
                handleUpdateTaskValue(item.id, newVal);
              } else {
                setTaskListItems(prev => prev.map(i => i.id === item.id ? { ...i, pfna: newVal, checked: newVal === 'Pass' ? 1 : 0, checked_by_name: newVal ? (user?.name || '') : null, checked_at: newVal ? new Date().toISOString() : null } : i));
                handleUpdateTaskPfna(item.id, newVal);
              }
            };
            return (
              <React.Fragment key={item.id}>
              {showSectionHeader && (() => {
                const sectionItems = taskListItems.filter(i => i.section === item.section);
                const sectionChecked = sectionItems.filter(i => i.checked || (i.item_type === 'checkbox' ? i.value === 'Pass' : (i.pfna === 'Pass' || i.pfna === 'Fail' || i.pfna === 'N/A'))).length;
                return (
                <div style={{ padding: '10px 16px', background: 'rgba(37,99,235,0.06)', borderBottom: '1px solid var(--border)', marginTop: idx > 0 ? 12 : 0, borderTop: idx > 0 ? '2px solid var(--border)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.section}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: sectionChecked === sectionItems.length ? '#10b981' : 'var(--text-muted)' }}>
                    {sectionChecked}/{sectionItems.length} completed
                  </span>
                </div>
                );
              })()}
              <div style={{ borderBottom: '1px solid var(--border)', background: pfnaVal === 'Fail' ? 'rgba(239,68,68,0.03)' : 'transparent' }}>
                <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', padding: isMobile ? '10px 12px' : '8px 16px', minHeight: 40, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 0 }}>
                  {/* Task label row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: isMobile ? '100%' : undefined, flex: isMobile ? undefined : 1, minWidth: 0, paddingRight: isMobile ? 0 : 8 }}>
                    <span style={{ width: 28, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', flexShrink: 0 }}>{idx + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: pfnaVal ? '#6b7280' : 'var(--text-primary)' }}>{item.label}</span>
                        {!!item.required && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.1)', color: '#dc2626', fontWeight: 600, border: '1px solid rgba(220,38,38,0.2)' }}>Required</span>}
                        {!!item.require_photo && <FiCamera size={11} color="#2563eb" />}
                      </div>
                      {!!item.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{item.description}</div>}
                      {item.item_type !== 'checkbox' && (
                        canEdit ? (
                          <input type={item.item_type === 'number' ? 'number' : 'text'} value={item.value || ''} placeholder={item.item_type === 'number' ? '0' : 'Enter value...'}
                            onFocus={() => { editingFieldRef.current = true; }}
                            onChange={e => setTaskListItems(prev => prev.map(i => i.id === item.id ? { ...i, value: e.target.value } : i))}
                            onBlur={e => { editingFieldRef.current = false; handleUpdateTaskValue(item.id, e.target.value); }}
                            style={{ marginTop: 4, width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                        ) : item.value ? (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Value: {item.value}</div>
                        ) : null
                      )}
                    </div>
                  </div>
                  {/* Pass/Fail/N/A buttons — inline row on mobile */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 0, paddingLeft: isMobile ? 34 : 0, width: isMobile ? '100%' : undefined }}>
                    <div style={{ width: isMobile ? undefined : 56, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <button type="button" disabled={!canEdit} onClick={() => handlePfna('Pass')}
                        style={{ width: isMobile ? 36 : 28, height: isMobile ? 36 : 28, borderRadius: 6, border: `2px solid ${pfnaVal === 'Pass' ? '#10b981' : 'var(--border)'}`, background: pfnaVal === 'Pass' ? '#ecfdf5' : 'transparent', cursor: canEdit ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', padding: 0 }}>
                        {isMobile && pfnaVal !== 'Pass' ? <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>P</span> : pfnaVal === 'Pass' && <span style={{ color: '#10b981', fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{'\u2713'}</span>}
                      </button>
                    </div>
                    <div style={{ width: isMobile ? undefined : 56, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <button type="button" disabled={!canEdit} onClick={() => handlePfna('Fail')}
                        style={{ width: isMobile ? 36 : 28, height: isMobile ? 36 : 28, borderRadius: 6, border: `2px solid ${pfnaVal === 'Fail' ? '#ef4444' : 'var(--border)'}`, background: pfnaVal === 'Fail' ? '#fef2f2' : 'transparent', cursor: canEdit ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', padding: 0 }}>
                        {isMobile && pfnaVal !== 'Fail' ? <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>F</span> : pfnaVal === 'Fail' && <span style={{ color: '#ef4444', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{'\u2717'}</span>}
                      </button>
                    </div>
                    <div style={{ width: isMobile ? undefined : 56, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <button type="button" disabled={!canEdit} onClick={() => handlePfna('N/A')}
                        style={{ width: isMobile ? 36 : 28, height: isMobile ? 36 : 28, borderRadius: 6, border: `2px solid ${pfnaVal === 'N/A' ? '#64748b' : 'var(--border)'}`, background: pfnaVal === 'N/A' ? '#f1f5f9' : 'transparent', cursor: canEdit ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', padding: 0 }}>
                        {pfnaVal === 'N/A' ? <span style={{ color: '#64748b', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>N/A</span> : isMobile && <span style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600 }}>N/A</span>}
                      </button>
                    </div>
                  </div>
                  {/* Comment */}
                  <div style={{ flex: isMobile ? undefined : 1, paddingLeft: isMobile ? 34 : 12, flexShrink: 0, width: isMobile ? '100%' : undefined, boxSizing: 'border-box' }}>
                    {canEdit ? (
                      <input type="text" value={item.comment || ''} placeholder="Comment..."
                        onFocus={() => { editingFieldRef.current = true; }}
                        onChange={e => {
                          let v = e.target.value;
                          if (v.length === 1) v = v.toUpperCase();
                          else if (v.length > 1) v = v.replace(/(^|[.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                          setTaskListItems(prev => prev.map(i => i.id === item.id ? { ...i, comment: v } : i));
                        }}
                        onBlur={e => { editingFieldRef.current = false; authFetch(`/api/issues/task-list/${item.id}/comment`, { method: 'PATCH', body: JSON.stringify({ comment: e.target.value }), headers: { 'Content-Type': 'application/json' } }).catch(() => {}); }}
                        style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                    ) : (
                      <span style={{ fontSize: 12, color: item.comment ? 'var(--text-secondary)' : 'var(--text-muted)', fontStyle: item.comment ? 'normal' : 'italic' }}>{item.comment || '-'}</span>
                    )}
                  </div>
                </div>
                {/* Photo row */}
                {(() => {
                  const photos = getPhotos(item);
                  const showRow = canEdit || photos.length > 0;
                  if (!showRow) return null;
                  return (
                    <div style={{ padding: '0 16px 6px 52px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {photos.map((p, pi) => (
                          <div key={pi} style={{ position: 'relative', display: 'inline-block' }}>
                            <img src={`/api/issues/file/${p}?token=${token}`} alt="" onClick={() => { setPhotoModal(`/api/issues/file/${p}?token=${token}`); setPhotoZoom(1); }}
                              style={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', cursor: 'pointer' }} />
                            {canEdit && (
                              <button onClick={() => handleDeleteTaskPhoto(item.id, p)}
                                style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, borderRadius: '50%', border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: 10, lineHeight: 1, fontWeight: 700 }}>×</button>
                            )}
                          </div>
                        ))}
                        {photos.length === 0 && !!item.require_photo && canEdit && <span style={{ fontSize: 11, color: '#dc2626' }}>Photo required</span>}
                        {canEdit && photos.length < 4 && (
                          <>
                            {isMobile && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>
                                <FiCamera size={10} /> Camera
                                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleTaskItemPhoto(item.id, e.target.files[0]); e.target.value = ''; }} />
                              </label>
                            )}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>
                              <FiUpload size={10} /> {photos.length > 0 ? 'Add' : 'Upload'}
                              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,image/heif" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleTaskItemPhoto(item.id, e.target.files[0]); e.target.value = ''; }} />
                            </label>
                          </>
                        )}
                      </div>
                      {taskPhotoUploading === item.id && (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ height: 3, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', background: '#2563eb', borderRadius: 2, width: `${taskPhotoProgress}%`, transition: 'width 0.2s' }} />
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Uploading... {taskPhotoProgress}%</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {(item.value || item.pfna) && item.checked_by_name && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '0 16px 4px 52px' }}>{item.checked_by_name} {item.checked_at ? `\u2022 ${formatDateTime(item.checked_at)}` : ''}</div>
                )}
              </div>
              </React.Fragment>
            );
          })}
              </div>

              {/* Per-operation additional comments */}
              {group.op && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
                  {opEditable ? (
                    <>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Additional Information</label>
                      <textarea value={group.op.additional_info || ''} maxLength={2000}
                        onFocus={() => { editingFieldRef.current = true; }}
                        onChange={e => {
                          let val = e.target.value;
                          // Auto-capitalize first letter of each sentence
                          val = val.replace(/(^|[.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                          setOperations(prev => prev.map(o => o.id === group.op.id ? { ...o, additional_info: val } : o));
                        }}
                        onBlur={e => { editingFieldRef.current = false; authFetch(`/api/issues/${issue.id}/operations/${group.op.id}/additional-info`, { method: 'PATCH', body: JSON.stringify({ additional_info: e.target.value }), headers: { 'Content-Type': 'application/json' } }).catch(() => {}); }}
                        placeholder="Enter any additional information..."
                        rows={2}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.4 }} />
                    </>
                  ) : (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Additional Information: </span>
                      <span style={{ color: group.op.additional_info ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: group.op.additional_info ? 'normal' : 'italic' }}>
                        {group.op.additional_info || 'N/A'}
                      </span>
                    </div>
                  )}
                </div>
              )}

            </div>
            );
          });
          })()}

          {/* Submit button — when inspection not yet completed */}
          {!hasFinalConfirmation && taskListItems.length > 0 && (() => {
            // For multi-op WOs, only validate items for the current (editable, non-complete) operation
            const multiOp = taskListByOp.filter(g => g.items.length > 0).length > 1;
            let currentOpItems = taskListItems;
            if (multiOp) {
              const currentGroup = taskListByOp.find(g => {
                if (!g.op) return false;
                const hasFinal = timeConfirmations.filter(tc => tc.operation_id === g.op.id).some(tc => tc.is_final);
                return g.op.status !== 'Complete' && !hasFinal;
              });
              if (currentGroup) currentOpItems = currentGroup.items;
            }
            const allAnswered = currentOpItems.every(i => {
              return i.item_type === 'checkbox' ? (i.value || i.checked) : !!(i.pfna || '');
            });
            const allPhotos = currentOpItems.filter(i => i.require_photo && getPhotos(i).length === 0).length === 0;
            const formValid = allAnswered && allPhotos;
            const remaining = currentOpItems.filter(i => !(i.item_type === 'checkbox' ? (i.value || i.checked) : i.pfna)).length;
            const missingPhotos = currentOpItems.filter(i => i.require_photo && getPhotos(i).length === 0).length;
            return (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {!formValid && (remaining > 0 ? `${remaining} item${remaining > 1 ? 's' : ''} remaining` : '') + (remaining > 0 && missingPhotos > 0 ? ', ' : '') + (missingPhotos > 0 ? `${missingPhotos} photo${missingPhotos > 1 ? 's' : ''} required` : '')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <motion.button whileHover={formValid ? { scale: 1.02 } : {}} whileTap={formValid ? { scale: 0.98 } : {}}
                  disabled={!formValid}
                  onClick={() => {
                    setShowTimeConfirm(true);
                  }}
                  style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: formValid ? '#10b981' : '#94a3b8', color: '#fff', cursor: formValid ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700, boxShadow: formValid ? '0 2px 8px rgba(16,185,129,0.3)' : 'none', opacity: formValid ? 1 : 0.6, transition: 'all 0.2s' }}>
                  <FiCheckSquare size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Submit Inspection
                </motion.button>
              </div>
            </div>
            );
          })()}

          {/* History Compare panel — only for PM02 with a template, sitting
              under the task-list tab. Lazy-loads on first expand. */}
          {issue.type === 'PM02' && issue.task_list_template_id && (() => {
            const PFNA_COLORS = { Pass: '#10b981', Fail: '#dc2626', 'N/A': '#94a3b8' };
            const fmtDate = (iso) => { if (!iso) return '—'; const d = new Date(iso); if (isNaN(d.getTime())) return '—'; return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }); };

            const loadHistory = (showAll) => {
              setTaskHistoryLoading(true);
              const qs = showAll ? '?include_all=true' : '?limit=12';
              authFetch(`/api/issues/${resolvedId}/task-history${qs}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => { setTaskHistory(data); setTaskHistoryLoading(false); })
                .catch(() => setTaskHistoryLoading(false));
            };

            const onToggleExpand = () => {
              const next = !taskHistoryExpanded;
              setTaskHistoryExpanded(next);
              if (next && !taskHistory) loadHistory(false);
            };
            const onToggleShowAll = () => {
              const next = !taskHistoryShowAll;
              setTaskHistoryShowAll(next);
              loadHistory(next);
            };

            // Sparkline for numeric rows — inline SVG, no deps
            const sparkline = (values) => {
              const nums = values.map(v => v == null || v === '' ? null : Number(String(v).replace(/[^0-9.\-]/g, '')))
                                 .map(n => Number.isFinite(n) ? n : null);
              const present = nums.map((n, i) => ({ n, i })).filter(p => p.n != null);
              if (present.length < 2) return null;
              const min = Math.min(...present.map(p => p.n));
              const max = Math.max(...present.map(p => p.n));
              const range = max - min || 1;
              const W = 80, H = 18;
              // Reverse order so left=oldest, right=newest (mirror the
              // newest-left columns by reversing the array here)
              const reversed = present.slice().reverse();
              const stepX = reversed.length > 1 ? W / (reversed.length - 1) : 0;
              const pts = reversed.map((p, idx) => {
                const x = Math.round(idx * stepX);
                const y = Math.round(H - ((p.n - min) / range) * H);
                return `${x},${y}`;
              }).join(' ');
              const last = reversed[reversed.length - 1];
              const trend = reversed.length >= 2 && last.n < reversed[0].n ? '#dc2626' : '#10b981';
              return (
                <svg width={W} height={H} style={{ verticalAlign: 'middle' }}>
                  <polyline fill="none" stroke={trend} strokeWidth={1.5} points={pts} />
                </svg>
              );
            };

            return (
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
                <button onClick={onToggleExpand}
                  style={{ width: '100%', padding: '14px 20px', background: 'transparent', border: 'none', borderBottom: taskHistoryExpanded ? '1px solid var(--border)' : 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {taskHistoryExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                    History compare — previous runs of this PM02
                  </span>
                  {taskHistoryExpanded && taskHistory?.previous_runs?.length > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>
                      {taskHistory.previous_runs.length} run{taskHistory.previous_runs.length === 1 ? '' : 's'}
                    </span>
                  )}
                </button>

                {taskHistoryExpanded && (
                  <div style={{ padding: 16 }}>
                    {taskHistoryLoading ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
                    ) : !taskHistory || taskHistory.previous_runs.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                        No previous completions of this PM02 against this asset yet — once another run TECOs, comparisons will appear here.
                      </div>
                    ) : (() => {
                      const runs = taskHistory.previous_runs;
                      const groups = {};
                      for (const it of taskHistory.items) {
                        const sec = it.section || '—';
                        if (!groups[sec]) groups[sec] = [];
                        groups[sec].push(it);
                      }
                      return (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
                            <button onClick={onToggleShowAll}
                              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              {taskHistoryShowAll ? 'Show last 12' : 'Show all'}
                            </button>
                          </div>
                          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr>
                                  <th style={{ position: 'sticky', left: 0, background: 'var(--bg-card)', zIndex: 2, textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border)', fontWeight: 700, color: 'var(--text-secondary)', minWidth: 240 }}>Task</th>
                                  {runs.map(r => (
                                    <th key={r.id} style={{ padding: '8px 10px', borderBottom: '2px solid var(--border)', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: 70 }}>
                                      <a href={`/work-orders/${r.wo_number}`}
                                        style={{ color: '#2563eb', textDecoration: 'none' }}
                                        title={`${r.wo_number}${r.user_name ? ' · ' + r.user_name : ''}`}>
                                        {fmtDate(r.teco_at)}
                                      </a>
                                    </th>
                                  ))}
                                  <th style={{ padding: '8px 10px', borderBottom: '2px solid var(--border)', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Trend</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.keys(groups).map(sec => (
                                  <React.Fragment key={sec}>
                                    {sec !== '—' && (
                                      <tr>
                                        <td colSpan={runs.length + 2} style={{ padding: '10px 10px 4px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                          {sec}
                                        </td>
                                      </tr>
                                    )}
                                    {groups[sec].map(it => {
                                      const orderedValues = runs.map(r => it.values_by_run[r.id] || null);
                                      return (
                                        <tr key={it.template_item_id}>
                                          <td style={{ position: 'sticky', left: 0, background: 'var(--bg-card)', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                                            {it.title}
                                          </td>
                                          {orderedValues.map((v, idx) => {
                                            if (!v || (v.value == null && !v.pfna)) {
                                              return <td key={idx} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-muted)' }}>—</td>;
                                            }
                                            const pfnaColor = v.pfna && PFNA_COLORS[v.pfna];
                                            const label = v.value != null && v.value !== '' ? v.value : v.pfna;
                                            return (
                                              <td key={idx} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {pfnaColor ? (
                                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: `${pfnaColor}22`, color: pfnaColor, border: `1px solid ${pfnaColor}55`, fontSize: 11, fontWeight: 700 }}>{label}</span>
                                                ) : (
                                                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{label}</span>
                                                )}
                                              </td>
                                            );
                                          })}
                                          <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                                            {it.is_numeric ? sparkline(orderedValues.map(v => v?.value)) : null}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Comments Tab */}
      {woTab === 'comments' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {comments.map(c => {
              const isSys = !!c.is_system;
              return (
              <div key={c.id} style={{ display: 'flex', gap: isSys ? 10 : 12, padding: isSys ? '8px 12px' : 14, background: isSys ? 'transparent' : 'var(--bg-input)', borderRadius: 10, border: isSys ? '1px solid var(--border)' : '1px solid var(--border)', borderLeft: isSys ? '3px solid #f59e0b' : '1px solid var(--border)', opacity: isSys ? 0.85 : 1 }}>
                {!isSys && c.added_by_avatar ? (
                  <img src={`/api/users/avatar/${c.added_by_avatar}?token=${token}`} alt=""
                    style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : !isSys ? (
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: c.added_by_avatar_color || 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0
                  }}>{(() => { const p = (c.added_by_name || '?').trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0][0].toUpperCase(); })()}</div>
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>📦</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isSys ? 2 : 4 }}>
                    <span style={{ fontWeight: 700, fontSize: isSys ? 12 : 13, color: 'var(--text-primary)' }}>{c.added_by_name}</span>
                    {isSys && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: 'rgba(245,158,11,0.1)', color: '#d97706' }}>Material Update</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDateTime(c.created_at)}</span>
                    {c.edited_at && <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>(edited {formatDateTime(c.edited_at)})</span>}
                    {c.added_by === user?.id && issue.status !== 'TECO' && issue.type !== 'PM02' && editingCommentId !== c.id && (
                      <button onClick={() => { setEditingCommentId(c.id); setEditingCommentText(c.comment); }}
                        style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, marginLeft: 'auto' }}>Edit</button>
                    )}
                  </div>
                  {editingCommentId === c.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* Focus on open with the caret at the end, and hide the
                          floating action bar while focused (same as the new
                          comment composer) so it can't sit over the textarea. */}
                      <textarea value={editingCommentText} onChange={e => setEditingCommentText(e.target.value)}
                        autoFocus
                        onFocus={e => {
                          setCommentFocused(true);
                          const el = e.target;
                          el.style.height = 'auto'; el.style.height = el.scrollHeight + 2 + 'px';
                          el.setSelectionRange(el.value.length, el.value.length);
                        }}
                        onBlur={() => setCommentFocused(false)}
                        onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 2 + 'px'; }}
                        className="note-text"
                        style={{ width: '100%', minHeight: 60, outline: 'none', resize: 'vertical', boxSizing: 'border-box', overflow: 'hidden', userSelect: 'text', WebkitUserSelect: 'text' }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleEditComment(c.id)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Save</button>
                        <button onClick={() => setEditingCommentId(null)} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {c.comment && <div className="rich-content note-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.comment}</div>}
                      {c.edited_at && c.original_comment && (
                        <div style={{ marginTop: 4 }}>
                          <button onClick={() => setShowOriginalComment(showOriginalComment === c.id ? null : c.id)}
                            style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#2563eb', textDecoration: 'underline' }}>
                            {showOriginalComment === c.id ? 'Hide original' : 'Show changes'}
                          </button>
                          {showOriginalComment === c.id && (
                            <div className="selectable" style={{ marginTop: 4, padding: 8, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
                              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Original comment:</div>
                              {c.original_comment}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {c.files?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      {c.files.map(f => <MediaViewer key={f.id} file={f} token={token} />)}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
            {comments.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No comments yet</div>}
          </div>

          {issue.type !== 'PM02' && (
          <form onSubmit={handleAddComment} style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <textarea value={newComment}
              onFocus={() => setCommentFocused(true)}
              onBlur={() => setCommentFocused(false)}
              onChange={e => {
                let v = e.target.value;
                if (v.length === 1) v = v.toUpperCase();
                else if (v.length > 1) v = v.replace(/(^|[.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                setNewComment(v);
              }}
              // Auto-grow: shrink to auto first, then expand to scrollHeight
              // so the box always matches what's typed.
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              placeholder="Add a comment..." rows={4} className="note-text"
              style={{
                width: '100%', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                minHeight: 90, overflow: 'hidden'
              }}
            />
            {(() => {
              // Enforce min 2 / max 2000 chars — matches the length limits
              // set in Personal Settings. Whitespace-only comments count as
              // empty. Files-only comments (photos/PDFs only) are allowed.
              const trimmedLen = (newComment || '').trim().length;
              const overMax = trimmedLen > 2000;
              const underMin = trimmedLen > 0 && trimmedLen < 2;
              const hasFilesOnly = trimmedLen === 0 && commentFiles.length > 0;
              const canSubmit = !submittingComment && (hasFilesOnly || (trimmedLen >= 2 && !overMax));
              return (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 11, color: overMax ? '#dc2626' : underMin ? '#d97706' : 'var(--text-muted)' }}>
                    {overMax
                      ? `Too long — ${trimmedLen} / 2000`
                      : underMin
                        ? 'At least 2 characters, please.'
                        : trimmedLen > 0 ? `${trimmedLen} / 2000` : hasFilesOnly ? 'File-only comment' : ''}
                  </div>
                  <button type="submit" disabled={!canSubmit} style={{
                    padding: '8px 16px', borderRadius: 6, border: 'none',
                    background: canSubmit ? '#2563eb' : '#94a3b8',
                    color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600,
                    opacity: canSubmit ? 1 : 0.6
                  }}>{submittingComment ? 'Sending...' : 'Submit Comment'}</button>
                </div>
              );
            })()}
          </form>
          )}
        </div>
      )}

      {/* History Tab */}
      {woTab === 'history' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, border: '1px solid var(--border)', marginBottom: 16, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiClock size={16} color="#2563eb" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Activity Log</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>Chronological record of all changes made to this work order</div>
            </div>
          </div>
          {history.filter(h => h.field !== 'comment').length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <FiClock size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>No activity recorded yet</div>
              <div style={{ fontSize: 12, marginTop: 3 }}>Updates to this work order will be tracked here.</div>
            </div>
          ) : (
            <div style={{ position: 'relative', paddingLeft: 22 }}>
              {/* Vertical timeline rail */}
              <div style={{ position: 'absolute', left: 11, top: 6, bottom: 6, width: 2, background: 'var(--border)', borderRadius: 1 }} />
              {history.filter(h => h.field !== 'comment').map((h, idx, arr) => {
                const fieldMeta = {
                  created: { label: 'Work order created', color: '#10b981', verb: 'initiated' },
                  status: { label: 'Status updated', color: h.new_value === 'Complete' || h.new_value === 'TECO' ? '#10b981' : '#f59e0b', verb: 'updated status' },
                  priority: { label: 'Priority updated', color: '#8b5cf6', verb: 'changed priority' },
                  assigned_to: { label: 'Assignment updated', color: '#2563eb', verb: 'reassigned' },
                  due_date: { label: 'Due date updated', color: '#d97706', verb: 'rescheduled due date' },
                  title: { label: 'Title updated', color: '#6366f1', verb: 'edited title' },
                  description: { label: 'Description updated', color: '#64748b', verb: 'revised description' },
                  frequency: { label: 'Frequency updated', color: '#0ea5e9', verb: 'adjusted frequency' },
                  documents: { label: 'Document activity', color: '#8b5cf6', verb: 'updated attachments' },
                  time_confirmation: { label: 'Time confirmation', color: '#059669', verb: 'logged time' },
                  reschedule: { label: 'Reschedule', color: '#d97706', verb: 'rescheduled' },
                  reschedule_reason: { label: 'Reschedule reason', color: '#d97706', verb: 'provided reschedule reason' },
                  approval_status: { label: 'Approval decision', color: h.new_value === 'Approved' ? '#10b981' : '#ef4444', verb: h.new_value === 'Approved' ? 'approved' : 'declined' },
                };
                const meta = fieldMeta[h.field] || { label: (h.field || 'Change').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), color: '#64748b', verb: 'updated' };
                const hInitials = (() => { const p = (h.user_name || '?').trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0][0].toUpperCase(); })();
                const valuePill = (text, color, muted) => (
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, background: muted ? 'var(--bg-input)' : `${color}14`, color: muted ? 'var(--text-muted)' : color, border: muted ? '1px solid var(--border)' : `1px solid ${color}30`, marginLeft: 4, marginRight: 4 }}>{text}</span>
                );
                return (
                  <div key={h.id} style={{ position: 'relative', display: 'flex', gap: 14, paddingBottom: idx < arr.length - 1 ? 18 : 0 }}>
                    {/* Timeline dot */}
                    <div style={{ position: 'absolute', left: -17, top: 8, width: 12, height: 12, borderRadius: '50%', background: meta.color, border: '3px solid var(--bg-card)', boxShadow: `0 0 0 2px ${meta.color}33` }} />
                    {/* Avatar */}
                    <div style={{ flexShrink: 0 }}>
                      {h.user_avatar ? (
                        <img src={`/api/users/avatar/${h.user_avatar}?token=${token}`} alt=""
                          style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: h.user_avatar_color || 'linear-gradient(135deg, #2563eb, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', border: '2px solid var(--border)' }}>{hInitials}</div>
                      )}
                    </div>
                    {/* Content card */}
                    <div style={{ flex: 1, minWidth: 0, background: 'var(--bg-input)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border-subtle, var(--border))' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{meta.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{formatDateTime(h.created_at)}</div>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }}>
                        <strong style={{ color: 'var(--text-primary)' }}>{h.user_name || 'System'}</strong>
                        {h.field === 'created' ? (
                          <span style={{ color: 'var(--text-secondary)' }}> created this work order{h.new_value ? ` (${h.new_value})` : ''}.</span>
                        ) : h.field === 'documents' ? (
                          <span style={{ color: 'var(--text-secondary)', wordBreak: 'break-word' }}> {h.new_value}</span>
                        ) : h.field === 'time_confirmation' ? (
                          <span style={{ color: 'var(--text-secondary)' }}> submitted a time confirmation of {valuePill(h.new_value, meta.color)}.</span>
                        ) : h.field === 'reschedule_reason' ? (
                          <span style={{ color: 'var(--text-secondary)' }}> recorded the reschedule reason as{valuePill(h.new_value, '#d97706')}.</span>
                        ) : h.field === 'status' ? (
                          <span style={{ color: 'var(--text-secondary)' }}> changed the status from{valuePill(h.old_value || 'none', '#64748b', true)}to{valuePill(h.new_value, meta.color)}.</span>
                        ) : h.field === 'priority' ? (
                          <span style={{ color: 'var(--text-secondary)' }}> changed the priority from{valuePill(h.old_value || 'none', '#64748b', true)}to{valuePill(h.new_value, meta.color)}.</span>
                        ) : h.field === 'description' ? (
                          <span style={{ color: 'var(--text-secondary)' }}> revised the description.</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}> {meta.verb} from{valuePill(h.old_value || '(none)', '#64748b', true)}to{valuePill(h.new_value || '(none)', meta.color)}.</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {showTimeConfirm && (
          <TimeConfirmModal
            user={user}
            operations={operations}
            teamMembers={myTeam}
            preSelectedOperationId={selectedOperation?.id}
            onSubmit={handleTimeConfirmSubmit}
            onClose={() => { setShowTimeConfirm(false); setSelectedOperation(null); }}
            loggedHours={
              (() => {
                const opId = selectedOperation?.id;
                if (!opId) return 0;
                return timeConfirmations
                  .filter(tc => tc.operation_id === opId && !tc.is_reversal)
                  .reduce((sum, tc) => sum + (parseFloat(tc.hours) || 0) + (parseFloat(tc.minutes) || 0) / 60, 0);
              })()
            }
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showReschedule && (
          <RescheduleModal
            currentDueDate={issue.due_date}
            authFetch={authFetch}
            onReschedule={(newDate, reason, comment) => {
              authFetch(`/api/issues/${resolvedId}`, {
                method: 'PUT',
                body: JSON.stringify({ due_date: newDate, reschedule_reason: reason, reschedule_comment: comment })
              }).then(r => {
                if (r.ok) {
                  r.json().then(updated => setIssue(updated));
                  authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
                  authFetch(`/api/issues/${resolvedId}/operations`).then(r => r.ok ? r.json() : []).then(setOperations);
                  setConfirmDialog({
                    title: 'Rescheduled',
                    rows: [
                      { label: 'New Date', value: new Date(newDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) },
                      { label: 'Reason', value: reason || 'N/A' },
                    ],
                    confirmLabel: 'OK', confirmColor: '#2563eb',
                    onConfirm: () => setConfirmDialog(null),
                  });
                }
              });
              setShowReschedule(false);
            }}
            onClose={() => setShowReschedule(false)}
          />
        )}
      </AnimatePresence>


      {/* Themed Confirmation Dialog */}
      <AnimatePresence>
        {cancelTcTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setCancelTcTarget(null); setCancelTcReason(''); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 400, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', padding: 24 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Cancel Time Confirmation</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>A reversal entry will be recorded. Please provide a reason.</p>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Reason *</label>
                <select value={cancelTcReason} onChange={e => setCancelTcReason(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                  <option value="">Select reason...</option>
                  <option value="Incorrect hours">Incorrect hours</option>
                  <option value="Wrong operation">Wrong operation</option>
                  <option value="Duplicate entry">Duplicate entry</option>
                  <option value="Not performed">Not performed</option>
                  <option value="Supervisor correction">Supervisor correction</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setCancelTcTarget(null); setCancelTcReason(''); }}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Back
                </button>
                <button disabled={!cancelTcReason}
                  onClick={() => handleCancelTc(cancelTcTarget, cancelTcReason)}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: cancelTcReason ? '#dc2626' : 'var(--bg-secondary)', color: cancelTcReason ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: cancelTcReason ? 'pointer' : 'not-allowed' }}>
                  Cancel Confirmation
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {confirmDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmDialog(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{confirmDialog.title}</h3>
                <button onClick={() => setConfirmDialog(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '18px 22px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {confirmDialog.rows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{row.label}</span>
                      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  {!confirmDialog.hideCancel && (
                    <button onClick={() => setConfirmDialog(null)}
                      style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                      Cancel
                    </button>
                  )}
                  <motion.button whileTap={{ scale: 0.97 }} onClick={confirmDialog.onConfirm}
                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: confirmDialog.confirmColor || 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {confirmDialog.confirmLabel || 'Confirm'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Edit Lock Error Popup */}
        {editLockError && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setEditLockError(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 400 }}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 400, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden', textAlign: 'center', padding: '30px 24px', position: 'relative' }}>
              <button onClick={() => setEditLockError(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <FiEdit2 size={24} color="#ef4444" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Edit Locked</h3>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{editLockError}</p>
              <button onClick={() => setEditLockError(null)}
                style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                OK
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* Photo Modal */}
        {photoModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPhotoModal(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, flexDirection: 'column' }}>
            <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ overflow: 'auto', maxWidth: '90vw', maxHeight: '75vh', borderRadius: 8 }}>
                <img src={photoModal} alt="" style={{ transform: `scale(${photoZoom})`, transformOrigin: 'center center', transition: 'transform 0.2s', maxWidth: photoZoom <= 1 ? '90vw' : 'none', maxHeight: photoZoom <= 1 ? '75vh' : 'none', display: 'block' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => setPhotoZoom(z => Math.max(0.5, z - 0.25))}
                  style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                  <FiZoomOut size={16} />
                </button>
                <button onClick={() => setPhotoZoom(1)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, minWidth: 50, textAlign: 'center' }}>
                  {Math.round(photoZoom * 100)}%
                </button>
                <button onClick={() => setPhotoZoom(z => Math.min(4, z + 0.25))}
                  style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                  <FiZoomIn size={16} />
                </button>
                <button onClick={async () => { try { const r = await fetch(photoModal); const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = ''; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u); } catch { window.open(photoModal, '_blank'); } }}
                  style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                  <FiDownload size={16} /> Download
                </button>
                <button onClick={() => { const w = window.open(photoModal); w.onload = () => w.print(); }}
                  style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
                  <FiPrinter size={16} /> Print
                </button>
              </div>
            </div>
            <button onClick={() => setPhotoModal(null)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiX size={20} />
            </button>
          </motion.div>
        )}


        {/* Add Operation Modal */}
        {showAddOpModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: window.innerWidth < 480 ? 8 : 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              style={{ background: 'var(--bg-card)', borderRadius: window.innerWidth < 480 ? 12 : 16, width: '100%', maxWidth: 520, maxHeight: '90vh', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {addOpSuccess ? (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <FiCheck size={32} color="#10b981" />
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                    {addOpSuccess.count > 1 ? `${addOpSuccess.count} Operations Submitted` : 'Operation Submitted'}
                  </h3>
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                    Has been sent to supervisor for review and approval.
                  </p>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => { setShowAddOpModal(false); setAddOpSuccess(null); setPendingOps([{ _id: 0, title: '', date: '', hours: 0, minutes: 0, description: '' }]); pendingOpIdRef.current = 1; }}
                    style={{ padding: '12px 32px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
                    Done
                  </motion.button>
                </div>
              ) : (
              <>
              {/* Header */}
              <div style={{ padding: window.innerWidth < 480 ? '14px 16px' : '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FiPlus size={20} color="#2563eb" />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>New Operations ({pendingOps.length})</h3>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Add tasks or steps — each sent to supervisor for approval</p>
                </div>
                <button onClick={() => { setShowAddOpModal(false); setPendingOps([{ _id: 0, title: '', date: '', hours: 0, minutes: 0, description: '' }]); pendingOpIdRef.current = 1; }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              {/* Body - scrollable */}
              <div style={{ padding: window.innerWidth < 480 ? '12px 14px' : '16px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pendingOps.map((op, idx) => (
                  <div key={op._id} style={{ padding: 16, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 4 }}>
                        OP {String((operations.length + idx + 1) * 10).padStart(4, '0')}
                      </span>
                      {pendingOps.length > 1 && (
                        <button onClick={() => setPendingOps(prev => prev.filter((_, i) => i !== idx))}
                          style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 10 }}>
                          <FiTrash2 size={12} />
                        </button>
                      )}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input value={op.title} onChange={e => { const v = e.target.value.replace(/(^|\s)([a-z])/g, (m, p, l) => p + l.toUpperCase()); setPendingOps(prev => prev.map((o, i) => i === idx ? { ...o, title: v } : o)); }}
                        placeholder="Operation name..." autoFocus={idx === 0}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                        onFocus={e => e.target.style.borderColor = '#2563eb'} onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Description</label>
                      <textarea value={op.description} onChange={e => { let v = e.target.value; v = v.replace(/(^|[.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase()); setPendingOps(prev => prev.map((o, i) => i === idx ? { ...o, description: v } : o)); }}
                        placeholder="Describe the operation..." rows={2}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4, transition: 'border-color 0.2s' }}
                        onFocus={e => e.target.style.borderColor = '#2563eb'} onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 120px' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Time <span style={{ color: '#ef4444' }}>*</span></label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <select value={op.hours} onChange={e => setPendingOps(prev => prev.map((o, i) => i === idx ? { ...o, hours: parseInt(e.target.value) || 0 } : o))}
                            style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                            {Array.from({ length: 13 }, (_, i) => i).map(h => <option key={h} value={h}>{h}h</option>)}
                          </select>
                          <select value={op.minutes} onChange={e => setPendingOps(prev => prev.map((o, i) => i === idx ? { ...o, minutes: parseInt(e.target.value) || 0 } : o))}
                            style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                            <option value="0">0m</option><option value="15">15m</option><option value="30">30m</option><option value="45">45m</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ flex: '1 1 140px' }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Date <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="date" value={op.date} onChange={e => setPendingOps(prev => prev.map((o, i) => i === idx ? { ...o, date: e.target.value } : o))} min={new Date().toISOString().split('T')[0]}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => setPendingOps(prev => [...prev, { _id: pendingOpIdRef.current++, title: '', date: '', hours: 0, minutes: 0, description: '' }])}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '10px 14px', borderRadius: 10, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#2563eb', width: '100%' }}>
                  <FiPlus size={14} /> Add Another Operation
                </button>
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.15)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiInfo size={14} color="#d97706" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: '#92400e' }}>Each operation will be sent to a supervisor for approval.</span>
                </div>
              </div>
              {/* Footer */}
              {(() => {
                const allValid = pendingOps.every(op => op.title.trim() && (op.hours > 0 || op.minutes > 0) && op.date);
                return (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: window.innerWidth < 480 ? '12px 14px' : '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--bg-card)', flexShrink: 0, flexWrap: 'wrap' }}>
                <button onClick={() => { setShowAddOpModal(false); setPendingOps([{ _id: 0, title: '', date: '', hours: 0, minutes: 0, description: '' }]); pendingOpIdRef.current = 1; }}
                  style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                  Cancel
                </button>
                <motion.button whileTap={allValid ? { scale: 0.97 } : {}}
                  disabled={!allValid}
                  onClick={async () => {
                    if (!allValid) return;
                    const results = [];
                    for (const op of pendingOps) {
                      const estHours = (op.hours || 0) + (op.minutes || 0) / 60;
                      const res = await authFetch(`/api/issues/${resolvedId}/operations`, { method: 'POST', body: JSON.stringify({ title: op.title.trim(), planned_date: op.date || undefined, estimated_hours: estHours || undefined, additional_info: op.description.trim() || undefined }) });
                      if (res.ok) { const newOp = await res.json(); results.push(newOp); }
                    }
                    if (results.length > 0) {
                      playSuccess();
                      setOperations(prev => [...prev, ...results]);
                      setAddOpSuccess({ count: results.length });
                    }
                  }}
                  style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: allValid ? '#2563eb' : '#94a3b8', color: '#fff', cursor: allValid ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600, opacity: allValid ? 1 : 0.5, boxShadow: allValid ? '0 4px 12px rgba(37,99,235,0.3)' : 'none' }}>
                  <FiPlus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Submit {pendingOps.length > 1 ? `${pendingOps.length} Operations` : 'Operation'}
                </motion.button>
              </div>
                );
              })()}
              </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Operation Modal */}
      <AnimatePresence>
        {showEditOpModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 560, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Edit Operation</h3>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{showEditOpModal.operation_number} — {issue.wo_number}</div>
                </div>
                <button onClick={() => setShowEditOpModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Operation Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input value={editOpForm.title} onChange={e => setEditOpForm(f => ({ ...f, title: e.target.value }))} autoFocus
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Assign To - custom dropdown with avatar */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Assign To</label>
                  {(() => {
                    const selectedUser = users.find(u => String(u.id) === String(editOpForm.assigned_to));
                    const getInits = (name) => { if (!name) return '?'; const p = name.trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0][0].toUpperCase(); };
                    const filteredUsers = users.filter(u => !u.disabled && (!editOpUserSearch || u.name.toLowerCase().includes(editOpUserSearch.toLowerCase())));
                    return (
                      <div style={{ position: 'relative' }}>
                        <div onClick={() => setEditOpUserDropdownOpen(!editOpUserDropdownOpen)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxSizing: 'border-box', minHeight: 40 }}>
                          {selectedUser ? (
                            <>
                              {selectedUser.avatar ? (
                                <img src={`/api/users/avatar/${selectedUser.avatar}`} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                              ) : (
                                <div style={{ width: 22, height: 22, borderRadius: '50%', background: selectedUser.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                  {getInits(selectedUser.name)}
                                </div>
                              )}
                              <span style={{ fontWeight: 600 }}>{selectedUser.name}</span>
                            </>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>Select team member...</span>
                          )}
                          <FiChevronDown size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)', transform: editOpUserDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </div>
                        {editOpUserDropdownOpen && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 240, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                              <div style={{ position: 'relative' }}>
                                <FiSearch size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                <input value={editOpUserSearch} onChange={e => setEditOpUserSearch(e.target.value)} placeholder="Search..." autoFocus
                                  style={{ width: '100%', padding: '6px 10px 6px 26px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                              </div>
                            </div>
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                              {editOpForm.assigned_to && (
                                <div onClick={() => { setEditOpForm(f => ({ ...f, assigned_to: '' })); setEditOpUserDropdownOpen(false); setEditOpUserSearch(''); setEditOpCapacity([]); }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  Clear selection
                                </div>
                              )}
                              {filteredUsers.length === 0 && <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No users found</div>}
                              {filteredUsers.map(u => (
                                <div key={u.id} onClick={() => {
                                  setEditOpForm(f => ({ ...f, assigned_to: String(u.id) }));
                                  setEditOpUserDropdownOpen(false);
                                  setEditOpUserSearch('');
                                  // Load capacity for selected user
                                  authFetch(`/api/issues/user-capacity-daily?user_id=${u.id}&weeks=${editOpCapRange}`)
                                    .then(r => r.json()).then(setEditOpCapacity).catch(() => setEditOpCapacity([]));
                                }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.1s', background: String(u.id) === String(editOpForm.assigned_to) ? 'rgba(37,99,235,0.08)' : 'transparent' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.06)'}
                                  onMouseLeave={e => e.currentTarget.style.background = String(u.id) === String(editOpForm.assigned_to) ? 'rgba(37,99,235,0.08)' : 'transparent'}>
                                  {u.avatar ? (
                                    <img src={`/api/users/avatar/${u.avatar}`} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                  ) : (
                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: u.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                      {getInits(u.name)}
                                    </div>
                                  )}
                                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{u.name}</span>
                                  {String(u.id) === String(editOpForm.assigned_to) && <FiCheck size={14} color="#2563eb" />}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Estimated Time</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <select value={editOpForm.est_hours} onChange={e => { const h = parseInt(e.target.value) || 0; setEditOpForm(f => { if (f.planned_date && editOpCapacity.length > 0) { const d = editOpCapacity.find(d => d.date === f.planned_date); if (d && d.available > 0 && (d.allocated + h + f.est_minutes / 60) > d.available) return { ...f, est_hours: h, planned_date: '' }; } return { ...f, est_hours: h }; }); }}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                        {Array.from({ length: 13 }, (_, i) => i).map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Hours</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <select value={editOpForm.est_minutes} onChange={e => { const m = parseInt(e.target.value) || 0; setEditOpForm(f => { if (f.planned_date && editOpCapacity.length > 0) { const d = editOpCapacity.find(d => d.date === f.planned_date); if (d && d.available > 0 && (d.allocated + f.est_hours + m / 60) > d.available) return { ...f, est_minutes: m, planned_date: '' }; } return { ...f, est_minutes: m }; }); }}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                        {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: 'center' }}>Minutes</div>
                    </div>
                  </div>
                </div>

                {/* Capacity calendar - shown when a user is selected */}
                {editOpForm.assigned_to && editOpCapacity.length > 0 && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Planned Date — Click a day to set</label>
                    <div style={{ background: 'var(--bg-input)', borderRadius: 10, padding: 10, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Capacity for {users.find(u => String(u.id) === String(editOpForm.assigned_to))?.name}</div>
                        <select value={editOpCapRange} onChange={e => {
                          const w = parseInt(e.target.value); setEditOpCapRange(w);
                          authFetch(`/api/issues/user-capacity-daily?user_id=${editOpForm.assigned_to}&weeks=${w}`)
                            .then(r => r.json()).then(setEditOpCapacity).catch(() => {});
                        }}
                          style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 11 }}>
                          <option value={1}>This week</option><option value={2}>2 weeks</option><option value={4}>1 month</option>
                          <option value={8}>2 months</option><option value={13}>3 months</option><option value={26}>6 months</option><option value={52}>1 year</option><option value={104}>2 years</option><option value={260}>5 years</option>
                        </select>
                      </div>
                      {(() => {
                        const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                        const fmtH = v => { const snapped = Math.round(v * 4) / 4; const h = Math.floor(snapped); const m = Math.round((snapped - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                        const jobHours = (editOpForm.est_hours || 0) + (editOpForm.est_minutes || 0) / 60;
                        const selectedDate = editOpForm.planned_date;
                        const selectedWeekIdx = selectedDate ? editOpCapacity.findIndex(d => d.date === selectedDate) : -1;
                        const scrollToWeek = selectedWeekIdx >= 0 ? Math.floor(selectedWeekIdx / 7) : -1;
                        return Array.from({ length: Math.ceil(editOpCapacity.length / 7) }, (_, weekIdx) => {
                          const weekDays = editOpCapacity.slice(weekIdx * 7, (weekIdx + 1) * 7);
                          if (weekDays.length === 0) return null;
                          const weekLabel = new Date(weekDays[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                          return (
                            <div key={weekIdx} ref={weekIdx === scrollToWeek ? el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } : undefined} style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>{weekLabel}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weekDays.length}, 1fr)`, gap: 2 }}>
                                {weekDays.map(day => {
                                  const isPast = day.date < todayStr;
                                  const isToday = day.date === todayStr;
                                  const isDueDate = issue.due_date && day.date === issue.due_date.slice(0, 10);
                                  const reqDate = issue.due_date ? issue.due_date.slice(0, 10) : null;
                                  const isOff = day.offDuty && !isPast;
                                  const pct = day.available > 0 ? Math.round((day.allocated / day.available) * 100) : (day.allocated > 0 ? 100 : 0);
                                  const isFull = day.available > 0 && day.allocated >= day.available;
                                  const isOver = day.available > 0 && day.allocated > day.available;
                                  const wouldExceed = day.available > 0 && (day.allocated + jobHours) > day.available;
                                  const isWarn = pct >= 75 && !isFull && day.available > 0;
                                  const hasWork = day.allocated >= 0.25 && !isFull && !isWarn;
                                  const isOverCap = isFull || wouldExceed;
                                  const isCriticalBusy = issue.priority === 'Push' && day.allocated > 0 && day.available > 0;
                                  const isDisabled = isPast || isOverCap || isCriticalBusy;
                                  const isSelected = editOpForm.planned_date === day.date;
                                  const selColor = isSelected && isOff ? '#f59e0b' : isSelected && isOverCap ? '#ef4444' : '#2563eb';
                                  const bg = isSelected ? selColor : isOver ? 'rgba(153,27,27,0.85)' : isFull ? 'rgba(239,68,68,0.12)' : isDisabled ? (wouldExceed ? 'rgba(245,158,11,0.1)' : 'var(--bg-input)') : isDueDate ? 'rgba(16,185,129,0.1)' : isWarn ? 'rgba(245,158,11,0.08)' : hasWork ? 'rgba(14,165,233,0.1)' : isOff ? 'rgba(245,158,11,0.05)' : isToday ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)';
                                  const borderColor = isSelected ? selColor : isOver ? '#991b1b' : isFull ? '#ef4444' : isDisabled ? (wouldExceed ? '#f59e0b' : 'var(--border)') : isDueDate ? '#10b981' : isWarn ? '#f59e0b' : hasWork ? '#0ea5e9' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--border)';
                                  const capText = `${fmtH(day.allocated)}/${fmtH(day.available)}`;
                                  return (
                                    <div key={day.date} onClick={() => { if (!isDisabled) setEditOpForm(f => ({ ...f, planned_date: day.date })); }}
                                      title={isDisabled ? `${day.day} ${day.date}\n${isCriticalBusy ? 'Push jobs can only be assigned to free days' : isOver ? 'OVER CAPACITY' : isFull ? 'FULLY BOOKED' : wouldExceed ? `Would exceed capacity (${fmtH(day.allocated)}+${fmtH(jobHours)} > ${fmtH(day.available)})` : 'Past date'}` : `${day.day} ${day.date}${isDueDate ? '\nWO DUE DATE' : ''}${isOff ? '\nOFF DUTY' : ''}\n${fmtH(day.allocated)} booked / ${fmtH(day.available)} capacity\n${day.jobs?.map(j => `${j.wo}: ${j.name} (${j.hours}h)`).join('\n') || 'No jobs'}`}
                                      style={{ padding: '2px 1px', borderRadius: 4, textAlign: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer', border: `${(isToday || isDueDate) && !isSelected ? '2px' : '1px'} solid ${borderColor}`, background: bg, opacity: isDisabled ? 0.35 : 1, transition: 'all 0.15s' }}>
                                      <div style={{ fontSize: 8, fontWeight: 600, color: isSelected ? '#fff' : isDueDate ? '#10b981' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--text-muted)' }}>{isDueDate && !isSelected ? 'DUE' : isOff && !isSelected ? 'OFF' : day.day}</div>
                                      <div style={{ fontSize: 9, fontWeight: 700, color: isSelected ? '#fff' : isDueDate ? '#10b981' : isToday ? '#2563eb' : 'var(--text-primary)' }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                      <div style={{ fontSize: 7, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : isDisabled ? 'var(--text-muted)' : isOver ? '#fecaca' : isFull ? '#ef4444' : isWarn ? '#f59e0b' : hasWork ? '#0ea5e9' : isOff ? '#f59e0b' : '#16a34a' }}>{capText}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Fallback date input when no user selected */}
                {!editOpForm.assigned_to && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Planned Date</label>
                    <input type="date" value={editOpForm.planned_date} onChange={e => setEditOpForm(f => ({ ...f, planned_date: e.target.value }))} min={new Date().toISOString().split('T')[0]}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                    />
                  </div>
                )}

                {editOpForm.planned_date && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Planned Date: <strong style={{ color: 'var(--text-primary)' }}>{new Date(editOpForm.planned_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                    <button onClick={() => setEditOpForm(f => ({ ...f, planned_date: '' }))} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Clear</button>
                  </div>
                )}
              </div>
              <div style={{ padding: '12px 24px 16px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                <button onClick={() => setShowEditOpModal(null)}
                  style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                  Cancel
                </button>
                <motion.button whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    if (!editOpForm.title.trim()) return;
                    const estHours = (editOpForm.est_hours || 0) + (editOpForm.est_minutes || 0) / 60;
                    authFetch(`/api/issues/${resolvedId}/operations/${showEditOpModal.id}`, {
                      method: 'PUT',
                      body: JSON.stringify({
                        title: editOpForm.title.trim(),
                        planned_date: editOpForm.planned_date || null,
                        estimated_hours: estHours || null,
                        assigned_to: editOpForm.assigned_to ? parseInt(editOpForm.assigned_to) : null
                      })
                    }).then(r => r.ok ? r.json() : null).then(updated => {
                      if (updated) { playSuccess(); setOperations(prev => prev.map(o => o.id === showEditOpModal.id ? updated : o)); }
                      setShowEditOpModal(null);
                    });
                  }}
                  style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: editOpForm.title.trim() ? 1 : 0.5 }}>
                  <FiSave size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Save Changes
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed bottom-right action buttons. Hidden while the user is
          inspecting (so Submit Inspection isn't covered) and while the
          comment composer is focused (Submit Comment lives at the
          bottom-right of the form and would otherwise sit under us).
          Also hidden while the time confirmation modal is open. On mobile
          the bar sits inline at the end of the page instead of floating,
          so it doesn't cover the work order content. */}
      <div style={{
        ...(isMobile
          ? { position: 'static', margin: '16px 0 calc(env(safe-area-inset-bottom, 0px) + 16px)', justifyContent: 'flex-end', flexWrap: 'wrap' }
          : { position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', right: 24, zIndex: 200 }),
        display: (inspecting || commentFocused || showTimeConfirm) ? 'none' : 'flex', gap: 8,
        background: 'var(--bg-card)', padding: '10px 14px', borderRadius: 12,
        border: '1px solid var(--border)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        {editMode ? (
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={async () => {
              if (resolvedId) authFetch(`/api/issues/${resolvedId}/edit-lock`, { method: 'DELETE' }).catch(() => {});
              setEditMode(false); setEditing(null);
              const r = await authFetch(`/api/issues/${resolvedId}`);
              if (r.ok) setIssue(await r.json());
              authFetch(`/api/issues/${resolvedId}/history`).then(r => r.ok ? r.json() : []).then(setHistory);
              playSuccess();
              setConfirmDialog({
                title: 'Work Order Updated',
                rows: [
                  { label: 'Work Order', value: issue.wo_number },
                  { label: 'Name', value: issue.name },
                  { label: 'Status', value: 'All changes have been saved successfully' }
                ],
                confirmLabel: 'OK', confirmColor: '#16a34a',
                onConfirm: () => setConfirmDialog(null),
                hideCancel: true,
              });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
              borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              boxShadow: '0 2px 8px rgba(22,163,74,0.3)'
            }}
          >
            <FiSave size={14} /> Save
          </motion.button>
        ) : !inspecting && (issue.assigned_to === user?.id || user?.permissions?.includes('admin_panel') || user?.permissions?.includes('supervisor_panel')) && (
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={async () => {
              try {
                const r = await authFetch(`/api/issues/${resolvedId}/edit-lock`, { method: 'POST' });
                if (r.ok) { setEditMode(true); setEditLockError(null); }
                else {
                  const data = await r.json();
                  setEditLockError(data.error || 'Someone else is editing');
                }
              } catch { setEditLockError('Failed to acquire edit lock'); }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
              borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)',
              color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600
            }}
          >
            <FiEdit2 size={14} /> Edit Work Order
          </motion.button>
        )}
        {!['PM01', 'PM02', 'PM03'].includes(issue.type) && timeConfirmations.length === 0 && operations.length <= 1 && (materials.length === 0 || materials.every(mat => {
          if (mat.material_type === 'labour') return mat.contacted && mat.labour_complete && mat.paid;
          const isInstalled = mat.ordered && mat.delivered && mat.installed;
          const isReturnDone = mat.return_type === 'return' ? (mat.return_packaged && mat.return_posted && mat.return_received && mat.return_refunded) :
            mat.return_type === 'warranty' ? (mat.return_packaged && mat.return_posted && mat.return_received && (mat.return_refunded || mat.return_new_part_sent)) : true;
          return isInstalled && isReturnDone;
        })) && (
        <motion.button
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => setShowDeleteConfirm(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
            borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.06)',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#dc2626'
          }}
        >
          <FiTrash2 size={14} /> Delete
        </motion.button>
        )}
      </div>
      {showSubWoModal && (
        <WorkOrderCreateModal
          onClose={() => setShowSubWoModal(false)}
          onCreated={() => { setShowSubWoModal(false); authFetch(`/api/issues/${resolvedId}/sub-work-orders`).then(r => r.ok ? r.json() : []).then(setSubWorkOrders); }}
          preselectedAssetId={issue.asset_id}
          parentWorkOrder={{ id: issue.id, wo_number: issue.wo_number, name: issue.name }}
        />
      )}
    </div>
  );
}

const RESCHEDULE_REASONS = [
  'Access Restricted',
  'Equipment In Use',
  'Higher Priority Work',
  'Parts Not Available',
  'Resource Unavailable',
  'Safety Concern',
  'Vendor Delay',
  'Waiting For Approval',
  'Weather Conditions',
  'Other',
];

function RescheduleModal({ currentDueDate, authFetch, onReschedule, onClose }) {
  const [selectedDate, setSelectedDate] = useState(currentDueDate || new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [jobLoadData, setJobLoadData] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    authFetch('/api/users/team').then(r => r.ok ? r.json() : []).then(setTeamMembers).catch(() => {});
  }, [authFetch]);

  const getWeekStart = useCallback((offset) => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1 + offset * 7); // Monday
    return d;
  }, []);

  const loadWeekData = useCallback((offset, userId) => {
    const start = getWeekStart(offset);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const userParam = userId ? `&user_id=${userId}` : '';
    authFetch(`/api/issues/job-load?start=${start.toISOString().split('T')[0]}&end=${end.toISOString().split('T')[0]}${userParam}`)
      .then(r => r.json())
      .then(data => setJobLoadData(data))
      .catch(() => {});
  }, [authFetch, getWeekStart]);

  useEffect(() => {
    loadWeekData(weekOffset, selectedUser);
  }, [weekOffset, selectedUser, loadWeekData]);

  const weekStart = getWeekStart(weekOffset);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getLoadForDate = (date) => {
    const entry = jobLoadData.find(e => e.date === date);
    return entry ? { count: entry.count, hours: parseFloat(entry.total_hours) || 0 } : { count: 0, hours: 0 };
  };

  const MAX_HOURS = 8;

  // Minimum date is today
  const minDate = new Date().toISOString().split('T')[0];

  const isDateTooSoon = (date) => date < minDate;

  const getDayColor = (date) => {
    const { hours } = getLoadForDate(date);
    if (date === selectedDate) return { bg: '#2563eb', text: '#fff', border: '#2563eb' };
    if (isDateTooSoon(date)) return { bg: 'rgba(156,163,175,0.08)', text: 'var(--text-muted)', border: 'var(--border-subtle)' };
    if (hours >= MAX_HOURS) return { bg: 'rgba(239,68,68,0.08)', text: '#ef4444', border: 'rgba(239,68,68,0.3)' };
    if (hours >= MAX_HOURS * 0.75) return { bg: 'rgba(245,158,11,0.08)', text: '#f59e0b', border: 'rgba(245,158,11,0.3)' };
    return { bg: 'var(--bg-input)', text: 'var(--text-primary)', border: 'var(--border)' };
  };

  const isDateFull = (date) => {
    if (isDateTooSoon(date)) return true;
    const { hours } = getLoadForDate(date);
    return hours >= MAX_HOURS;
  };

  const weekLabel = `${formatDate(weekDays[0])} - ${formatDate(weekDays[6])}`;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', borderRadius: 16, width: '95%', maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border)'
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
            <FiRefreshCw size={18} color="#d97706" /> Reschedule Work Order
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* User Filter */}
          {teamMembers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>
                View Capacity For
              </label>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={{
                width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-input)', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
                cursor: 'pointer'
              }}>
                <option value="">All Team Members</option>
                {teamMembers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}

          {/* Week Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <button onClick={() => setWeekOffset(w => w - 1)} style={{
              padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-card)', cursor: 'pointer', fontSize: 16, color: 'var(--text-primary)'
            }}>&larr;</button>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{weekLabel}</span>
            <button onClick={() => setWeekOffset(w => w + 1)} style={{
              padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-card)', cursor: 'pointer', fontSize: 16, color: 'var(--text-primary)'
            }}>&rarr;</button>
          </div>

          {/* Week Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 16 }}>
            {dayNames.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0' }}>{d}</div>
            ))}
            {weekDays.map((date, i) => {
              const colors = getDayColor(date);
              const { count, hours } = getLoadForDate(date);
              const dayNum = new Date(date).getDate();
              const isSelected = date === selectedDate;
              const full = isDateFull(date);
              return (
                <motion.div key={date} whileHover={full && !isSelected ? {} : { scale: 1.05 }} whileTap={full && !isSelected ? {} : { scale: 0.95 }}
                  onClick={() => { if (!full || isSelected) setSelectedDate(date); }}
                  style={{
                    padding: '10px 4px', borderRadius: 10, cursor: full && !isSelected ? 'not-allowed' : 'pointer', textAlign: 'center',
                    background: colors.bg, border: `2px solid ${colors.border}`,
                    transition: 'all 0.2s', opacity: full && !isSelected ? 0.5 : 1
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{dayNum}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)', marginTop: 4 }}>
                    {(() => { const s = Math.round(parseFloat(hours) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; })()} / {MAX_HOURS}h
                  </div>
                  {/* Capacity bar */}
                  <div style={{ height: 4, borderRadius: 2, background: isSelected ? 'rgba(255,255,255,0.3)' : 'var(--border)', marginTop: 6, overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(hours / MAX_HOURS * 100, 100)}%`, height: '100%', borderRadius: 2,
                      background: isSelected ? '#fff' : (hours >= MAX_HOURS ? '#ef4444' : hours >= MAX_HOURS * 0.75 ? '#f59e0b' : '#10b981')
                    }} />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--text-muted)', marginBottom: 8, justifyContent: 'center' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#10b981', marginRight: 3 }} />Available</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#f59e0b', marginRight: 3 }} />Nearly Full</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#ef4444', marginRight: 3 }} />Full</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#2563eb', marginRight: 3 }} />Selected</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 16 }}>
            Minimum 7 days from today
          </div>

          {/* Selected Date */}
          <div style={{
            padding: '12px 16px', borderRadius: 10, background: 'rgba(37,99,235,0.05)',
            border: '1px solid rgba(37,99,235,0.15)', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Selected: {formatDate(selectedDate)}</span>
            <span style={{ fontSize: 12, color: isDateFull(selectedDate) ? '#ef4444' : '#2563eb', fontWeight: 600 }}>
              {(() => { const v = getLoadForDate(selectedDate).hours; const s = Math.round(parseFloat(v) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; })()} / {MAX_HOURS}h ({getLoadForDate(selectedDate).count} jobs)
            </span>
          </div>

          {/* Reschedule Reason */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>
              Reason <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select value={reason} onChange={e => setReason(e.target.value)} style={{
              width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg-input)', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
              appearance: 'none', cursor: 'pointer'
            }}>
              <option value="">Select a reason...</option>
              {RESCHEDULE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {/* Comment */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>
              Comment
            </label>
            <textarea
              value={comment}
              onChange={e => {
                let v = e.target.value;
                if (v.length > 0) v = v.charAt(0).toUpperCase() + v.slice(1);
                v = v.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                setComment(v);
              }}
              placeholder="Additional details..."
              rows={2}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-input)', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg-card)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--text-primary)'
            }}>Cancel</button>
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => onReschedule(selectedDate, reason, comment)}
              disabled={!selectedDate || !reason || isDateTooSoon(selectedDate)}
              style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: '#d97706', color: '#fff',
                cursor: selectedDate && reason && !isDateTooSoon(selectedDate) ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600,
                opacity: selectedDate && reason && !isDateTooSoon(selectedDate) ? 1 : 0.5
              }}
            >Confirm Reschedule</motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function InfoItem({ icon: Icon, label, value, color, isOverdue, editable, onEdit, onClick }) {
  return (
    <div
      onClick={onClick || (editable ? onEdit : undefined)}
      style={{
        padding: '8px 12px', borderRadius: 8, cursor: (onClick || editable) ? 'pointer' : 'default',
        background: isOverdue ? '#fef2f2' : 'var(--bg-input)',
        border: `1px solid ${isOverdue ? '#fecaca' : 'var(--border-subtle)'}`,
        transition: 'background 0.2s'
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
        {Icon && <Icon size={11} />} {label} {editable && <FiEdit2 size={9} />}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: isOverdue ? '#dc2626' : (color || 'var(--text-primary)') }}>{value}</div>
    </div>
  );
}
